/**
 * Route copy for `src/routes/crm` (keys: `routes.crm.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  activityFeedPage: {
    errorStatus: 'Error {{status}}',
    couldNotLoadTheActivityFeed: 'Could not load the activity feed.',
    activityFeed: 'Activity feed',
    whoDidWhatInTheWorkspace: 'Who did what in the workspace — shares, assignments, enrichments, imports…',
    refresh: 'Refresh',
    theActivityFeedIsNotAvailable: 'The activity feed is not available on this environment.',
    noActivityYetTheTeamS: 'No activity yet. The team\'s actions will appear here.',
    all: 'All',
    loading: 'Loading…',
    loadMore: 'Load more',
    totalActivityItemSInTotal: '{{total}} activity item(s) in total.',
  },
  addByEmailModal: {
    pasteAtLeastOneValidEmail: 'Paste at least one valid email address.',
    couldNotPreviewDuplicatesTryAgain: 'Could not preview duplicates. Try again.',
    selectAtLeastOneNewContact: 'Select at least one new contact to create.',
    contactsCreatedButAddingThemTo: 'Contacts created, but adding them to the group failed.',
    lengthContactSAdded: '{{length}} contact(s) added.',
    couldNotCreateTheContacts: 'Could not create the contacts.',
    close: 'Close',
    cancel: 'Cancel',
    analyzing: 'Analyzing…',
    previewDuplicatesV: 'Preview duplicates{{v}}',
    adding: 'Adding…',
    addSizeContactS: 'Add {{size}} contact(s)',
    addContactsByEmail: 'Add contacts by email',
    pasteAListOfEmailAddresses: 'Paste a list of email addresses (separated by commas, semicolons, spaces or line breaks). We will check which ones already exist in your CRM before adding the new ones.',
    janeDoeExampleComJohnSmith: 'jane.doe@example.com\njohn.smith@example.com, contact@company.com',
    emailAddressList: 'Email address list',
    lengthValidEmailAddressEsDetected: '{{length}} valid email address(es) detected.',
    noValidEmailAddressDetectedYet: 'No valid email address detected yet.',
    alreadyExisting: 'Already existing',
    allTheseAddressesAlreadyExistIn: 'All these addresses already exist in your CRM — no new contact to create.',
    addToGroup: 'Add to group',
    none: 'None',
    lifecycleStage: 'Lifecycle stage',
    create: 'Create',
    email: 'Email',
    status: 'Status',
    createEmail: 'Create {{email}}',
    exists: 'Exists',
    new: 'New',
    theExistingAddressEsAlreadyPresent: 'The {{existing}} address(es) already present are ignored. Uncheck a “New” row to not create it.',
    addingComplete: 'Adding complete.',
    contactsCreatedCreated: 'Contacts created: {{created}}',
    addedToTheGroupAddedtogroup: 'Added to the group: {{addedToGroup}}',
    creationFailuresFailed: 'Creation failures: {{failed}}',
    findThemInTheContactList: 'Find them in the contact list.',
    viewContacts: 'View contacts',
    analyzingDuplicates: 'Analyzing duplicates…',
    new2: 'New',
  },
  addToGroupControl: {
    groupNameRequired: 'Group name required.',
    selectOrCreateAGroup: 'Select or create a group.',
    addedAddedToLabel: '{{added}} added to “{{label}}”',
    couldNotAddToTheGroup: 'Could not add to the group.',
    newGroupName: 'New group name',
    chooseAGroup: 'Choose a group',
    chooseAGroup2: '— Choose a group —',
    chooseExisting: 'Choose existing',
    new: '+ New',
    adding: 'Adding…',
    addToGroup: 'Add to group',
  },
  aiActions: {
    emailsOpened: 'Emails opened',
    emailsClicked: 'Emails clicked',
    responses: 'Responses',
    bounces: 'Bounces',
    socialInteractions: 'Social interactions',
    notes: 'Notes',
    enrichments: 'Enrichments',
    evaluations: 'Evaluations',
    smartFieldsUnavailable: 'Smart fields unavailable.',
    computingSmartFields: 'Computing smart fields…',
    smartFields: 'Smart fields',
    lastInteraction: 'Last interaction',
    daysOfSilence: 'Days of silence',
    totalInteractions: 'Total interactions',
    firstInteraction: 'First interaction',
    lastType: 'Last type',
    teamTotal: 'Team total',
    aiUnavailableRightNowTryAgain: 'AI unavailable right now. Try again later.',
    close: 'Close',
    theAiIsAnalyzingTheContext: 'The AI is analyzing the context…',
    couldNotComputeTheLabelField: 'Could not compute the “{{label}}” field.',
    aiAssistant: 'AI assistant',
    aiRecap: 'AI recap',
    readOnly: 'Read-only',
    aiResearchEnrichment: 'AI research + enrichment',
    aiResearch: 'AI research',
    aiEvaluation: 'AI evaluation',
    recommendations: 'Recommendations',
    framework: 'Framework:',
    summary: 'Summary',
    reopenToRecompute: '(reopen to recompute)',
    keyPoints: 'Key points',
    nextSteps: 'Next steps',
    aiResearchEnrichment2: 'AI research — enrichment',
    aPinnedNoteWasAddedAnd: 'A pinned note was added and the safe data merged into the enrichment.',
    inferredData: 'Inferred data',
    score: 'Score',
    fit: 'Fit',
    risk: 'Risk',
    sentiment: 'Sentiment',
    signals: 'Signals',
    analysis: 'Analysis',
    aiRecommendations: 'AI recommendations',
    bestActions: 'Best actions',
    talkingPoints: 'Talking points',
    magicFieldsAi: 'Magic fields (AI)',
    notComputed: 'Not computed',
    computing: 'Computing…',
    compute: 'Compute',
  },
  apiExtra: {
    featureNotConfiguredOnTheServer: 'Feature not configured on the server.',
    httpStreamStatus: 'HTTP stream {{status}}',
  },
  assignmentRuleDetailPage: {
    roundRobin: 'Round-robin',
    byTag: 'By tag',
    manual: 'Manual',
    membersRoundRobinOrder: 'Members (round-robin order)',
    nextRecipientCursor: 'Next recipient (cursor)',
    noMemberConfigured: 'No member configured.',
    cursor: 'Cursor',
    tagField: 'Tag field',
    tagsDefault: 'tags (default)',
    conditionsOwner: 'Conditions → owner',
    noTagOwnerMappingConfigured: 'No tag → owner mapping configured.',
    roundRobinFallbackTagsNotFound: 'Round-robin fallback (tags not found)',
    noneContactsStayUnassignedIfNo: 'None — contacts stay unassigned if no tag matches.',
    cursorFallback: 'Cursor (fallback)',
    emptyConfiguration: 'Empty configuration.',
    couldNotLoadTheRule: 'Could not load the rule.',
    crmSettings: 'CRM settings',
    assignmentRule: 'Assignment rule',
    loadingTheRule: 'Loading the rule…',
    tryAgain: 'Try again',
    thisAssignmentRuleDoesNotExist: 'This assignment rule does not exist or has been deleted.',
    backToSettings: 'Back to settings',
    active: 'Active',
    inactive: 'Inactive',
    strategy: 'Strategy',
    autoAssignmentOnCreation: 'Auto-assignment on creation',
    enabled: 'Enabled',
    disabled: 'Disabled',
    createdOn: 'Created on',
    modifiedOn: 'Modified on',
    thisRuleIsEligibleForAuto: 'This rule is eligible for auto-assigning new contacts without an owner. When several active rules are eligible, the oldest one (by creation date) is used first — there is no separate priority field on the server.',
    configurationStrategy: 'Configuration ({{strategy}})',
  },
  chartsExtra: {
    conversionShort: '{{pct}} conv.',
    trend: 'Trend',
    unavailable: 'unavailable',
    gauge: 'Gauge',
    progress: 'Progress',
    noData: 'No data.',
    breakdown: 'Breakdown',
    noData2: 'No data',
    total: 'total',
    mon: 'Mon',
    tue: 'Tue',
    wed: 'Wed',
    thu: 'Thu',
    fri: 'Fri',
    sat: 'Sat',
    sun: 'Sun',
    dayHHVInteractionS: '{{day}} {{h}}h — {{v}} interaction(s)',
    noTeamData: 'No team data.',
    changeVersusThePreviousPeriod: 'Change versus the previous period',
  },
  companiesPage: {
    theNameIsRequired: 'The name is required.',
    couldNotSave: 'Could not save.',
    editTheCompany: 'Edit the company',
    newCompany: 'New company',
    cancel: 'Cancel',
    saving: 'Saving…',
    save: 'Save',
    advancedFields: 'Advanced fields',
    identity: 'Identity',
    name: 'Name',
    domain: 'Domain',
    exampleCom: 'example.com',
    entityType: 'Entity type',
    address: 'Address',
    professional: 'Professional',
    industry: 'Industry',
    size: 'Size',
    segmentation: 'Segmentation',
    owner: 'Owner',
    note: 'Note',
    labels: 'Labels',
    socialNetworks: 'Social networks',
    addressCustomField: 'Address (custom field)',
    customFields: 'Custom fields',
    otherCustomFields: 'Other custom fields',
    type: 'Type',
    created: 'Created',
    actions: 'Actions',
    edit: 'Edit',
    delete: 'Delete',
    searchForACompany: 'Search for a company…',
    searchForACompany2: 'Search for a company',
    export: 'Export',
    newCompany2: '+ New company',
    noCompanies: 'No companies.',
    loading: 'Loading…',
    companies: 'Companies',
    industries: 'Industries',
    withWebsite: 'With website',
    network: 'network',
    deleteTheCompany: 'Delete the company',
    deleteName: 'Delete “{{name}}”?',
  },
  companyDetailDrawer: {
    couldNotUpdateTheOwner: 'Could not update the owner.',
    noNewDataFoundByThe: 'No new data found by the configured providers.',
    enrichmentUnavailableConfigureAProviderK: 'Enrichment unavailable — configure a provider key in Settings › Enrichment.',
    couldNotMerge: 'Could not merge.',
    couldNotArchive: 'Could not archive.',
    completeTheFirmographicsThroughTheConfig: 'Complete the firmographics through the configured enrichment cascade',
    enriching: 'Enriching…',
    enrich: 'Enrich',
    editTheCompany: 'Edit the company',
    domain: 'Domain',
    industry: 'Industry',
    size: 'Size',
    created: 'Created',
    address: 'Address',
    owner: 'Owner',
    mergeADuplicateSearchForThe: 'Merge a duplicate (search for the company to absorb)',
    mergeTheSelectedDuplicate: 'Merge the selected duplicate',
    archiveTheCompany: 'Archive the company',
    mergeTheDuplicate: 'Merge the duplicate',
    mergeThisDuplicateIntoTheCurrent: 'Merge this duplicate into the current company? This cannot be undone.',
    merge: 'Merge',
    archiveThisCompanyItWillBe: 'Archive this company? It will be hidden from the list.',
    archive: 'Archive',
  },
  companyMergePicker: {
    change: 'Change',
    searchForTheDuplicateCompany: 'Search for the duplicate company…',
    searchForTheDuplicateCompany2: 'Search for the duplicate company',
    searching: 'Searching…',
    noCompanyFound: 'No company found.',
  },
  components: {
    yourRoleDoesNotAllowEditing: 'Your role does not allow editing CRM data.',
    readOnly: 'Read-only',
    actionFailedTryAgain: 'Action failed. Try again.',
    oneMoment: 'One moment…',
    couldNotSaveTryAgain: 'Could not save. Try again.',
    cancel: 'Cancel',
    saving: 'Saving…',
    close: 'Close',
    score: 'Score',
    leadScore: 'Lead score',
    listDisplay: 'List display',
    table: 'Table',
    cards: 'Cards',
    loading: 'Loading…',
    delete: 'Delete',
    save: 'Save',
  },
  contactBulkEnrichModal: {
    bulkEnrichmentFailed: 'Bulk enrichment failed.',
    enrichTheSelectionLength: 'Enrich the selection ({{length}})',
    close: 'Close',
    aiResearch: 'AI research…',
    insufficientAiCreditsForPartOf: 'Insufficient AI credits for part of the selection — the AI step was blocked.',
    topUp: 'Top up',
    openAContactToChooseThe: 'Open a contact to choose the fields to apply (email, phone, networks…).',
    contact: 'Contact',
    emails: 'Emails',
    phone: 'Phone',
    networks: 'Networks',
    ai: 'AI',
    notFound: 'not found',
    blocked: 'blocked',
    vCr: '✓ {{v}} cr.',
  },
  contactCard: {
    leadScore: 'Lead score',
    leadScorePct: 'Lead score: {{pct}}',
    lastUpdated: 'Last updated',
    selectName: 'Select {{name}}',
    hotLead: '🔥 Hot lead',
    new: '✦ New',
    consent: '✓ Consent',
    doNotContact: '⦸ Do not contact',
    highPotentialPrioritize: 'High potential — prioritize',
    moderateEngagement: 'Moderate engagement',
    toWarmUp: 'To warm up',
    created: 'Created',
    creationDate: 'Creation date',
    owner: 'Owner',
    owner2: 'Owner',
    open: 'Open',
    enrich: 'Enrich',
    addToGroup: 'Add to group',
  },
  contactDetailDrawer: {
    activity: 'Activity',
    journey: 'Journey',
    deals: 'Deals',
    reminders: 'Reminders',
    notes: 'Notes',
    callLog: 'Call log',
    call: '📞 Call',
    someRelatedDataCouldNotBe: 'Some related data could not be loaded.',
    couldNotRemoveFromTheGroup: 'Could not remove from the group.',
    couldNotUpdateTheStage: 'Could not update the stage.',
    couldNotAddTheNote: 'Could not add the note.',
    couldNotDeleteTheNote: 'Could not delete the note.',
    couldNotCreateTheReminder: 'Could not create the reminder.',
    couldNotCompleteTheReminder: 'Could not complete the reminder.',
    couldNotAddTheCustomProperty: 'Could not add the custom property.',
    couldNotDeleteTheProperty: 'Could not delete the property.',
    findEmailPhoneSocialNetworksAnd: 'Find email, phone, social networks and address (AI + verification)',
    findEnrich: '✨ Find & Enrich',
    editTheContact: 'Edit the contact',
    groups: 'Groups',
    removeFromName: 'Remove from {{name}}',
    thisContactIsNotInAny: 'This contact is not in any group.',
    email: 'Email',
    phone: 'Phone',
    score: 'Score',
    owner: 'Owner',
    address: 'Address',
    socialNetworks: 'Social networks',
    description: 'Description',
    customProperties: 'Custom properties',
    deleteK: 'Delete {{k}}',
    noCustomProperties: 'No custom properties.',
    property: 'Property',
    customPropertyName: 'Custom property name',
    value: 'Value',
    customPropertyValue: 'Custom property value',
    add: 'Add',
    lifecycleStage: 'Lifecycle stage',
    noActivityRecorded: 'No activity recorded.',
    noAssociatedDeal: 'No associated deal.',
    reminderTitle: 'Reminder title',
    reminderDueDate: 'Reminder due date',
    reminderRecurrence: 'Reminder recurrence',
    noRecurrence: 'No recurrence',
    everyDay: 'Every day',
    everyWeek: 'Every week',
    everyMonth: 'Every month',
    everyYear: 'Every year',
    addTheReminder: 'Add the reminder',
    noReminders: 'No reminders.',
    finish: 'Finish',
    addANote: 'Add a note…',
    newNote: 'New note',
    mention: 'Mention:',
    removeTheMention: 'Remove the mention',
    mentionWillBeNotified: 'Mention (will be notified)',
    addTheNote: 'Add the note',
    noNotes: 'No notes.',
    deleteTheNote: 'Delete the note',
    mergeADuplicateSearchForThe: 'Merge a duplicate (search for the contact to absorb)',
    mergeTheSelectedDuplicate: 'Merge the selected duplicate',
    deleteTheContact: 'Delete the contact',
    mergeTheDuplicate: 'Merge the duplicate',
    mergeThisDuplicateIntoTheCurrent: 'Merge this duplicate into the current contact? This cannot be undone.',
    merge: 'Merge',
    deleteThisContactThisCannotBe: 'Delete this contact? This cannot be undone.',
  },
  firmographics: {
    industry: 'Industry',
    size: 'Size',
    employeeCount: 'Exact headcount',
    employeesN: '{{n}} employees',
    industryPlaceholder: 'E.g. Software, Healthcare, Food aid',
    employeeCountPlaceholder: 'E.g. 42',
    sizeNone: 'Not specified',
    sizeRangeEmployees: '{{range}} employees',
    filterByIndustry: 'Filter by industry',
    filterBySize: 'Filter by size',
    allSizes: 'All sizes',
    industryFilterPlaceholder: 'Industry',
    organisationProfile: 'Organisation profile',
    notProvided: 'Not provided',
    reviewIndustry: 'Industry',
    reviewSize: 'Organisation size',
    employeesExact: '≈ {{n}} employees',
  },
  contactEnrichModal: {
    planIntro: 'Choose the search steps. The price of every paid step is shown before it runs; nothing is charged until you click “Run the search”.',
    planVerifyLabel: 'Email / phone verification (MX, SMTP, E.164)',
    planWebLabel: 'Organisation website (public contact page)',
    planProvidersLabel: 'Licensed data providers (Settings › Enrichment)',
    planAiLabel: 'AI research (inference)',
    planFree: 'free',
    planTotalFree: 'Cost of this search: free',
    planTotal: 'Maximum cost of this search: {{n}} credits',
    planSettleNote: 'A paid step is only charged when it returns a usable result.',
    planRun: 'Run the search',
    confidencePct: 'Confidence {{pct}}%',
    verifiedSource: 'Verified · {{source}}',
    verified: 'Verified',
    providerDataSource: 'Provider data · {{source}}',
    providerData: 'Provider data',
    aiInferredSource: 'AI-inferred · {{source}}',
    aiInferred: 'AI-inferred',
    noSuggestions: 'No suggestions.',
    theSearchFailedTryAgainLater: 'The search failed. Try again later.',
    searchTimedOut: 'The search took too long (timed out). Try again in a moment.',
    searchServiceUnavailable: 'A search service is temporarily unavailable. Try again in a moment.',
    retry: 'Retry',
    degradedStepsTitle: 'Some steps did not complete — the other results are still available:',
    stepLine: '{{step}}: {{status}}',
    stepProviders: 'Data providers',
    stepB2bVerify: 'Email / phone verification',
    stepAiSearch: 'AI research',
    stepWebSearch: 'Organisation website',
    stepKeyPerson: 'Key person',
    stepServicesClassify: 'Services and categories',
    statusTimeout: 'timed out',
    statusError: 'error',
    statusUnavailable: 'unavailable',
    statusBudgetExhausted: 'skipped (time budget spent)',
    applyingTheFieldsFailed: 'Applying the fields failed.',
    findEnrichV: 'Find & Enrich{{v}}',
    cancel: 'Cancel',
    applying: 'Applying…',
    applyToTheContactSelectedcount: 'Apply to the contact ({{selectedCount}})',
    applyToTheContact: 'Apply to the contact',
    aiResearch: 'AI research…',
    insufficientAiCredits: 'Insufficient AI credits',
    theAiResearchStepWasBlocked: '— the AI research step was blocked.',
    requiredRequiredAvailableAvailableCredit: 'Required {{required}} · available {{available}} credit(s).',
    topUp: 'Top up',
    aiResearchDoneCostChargedCredit: '✨ AI research done · cost {{charged}} credit(s)',
    remainingBalanceAvailable: ' · remaining balance {{available}}',
    aiUnavailableRightNowOnlyVerified: 'AI unavailable right now — only verified results are shown.',
    emails: 'Emails',
    phones: 'Phones',
    socialNetworks: 'Social networks',
    address: 'Address',
    company: 'Company',
    positionRole: 'Position / Role',
    description: 'Description',
    inferredAiSuggestionToVerifyNo: 'Inferred = AI suggestion to verify. No data is collected by scraping; only AI inference and email/phone verification are used.',
  },
  contactFields: {
    nOfMax: '{{n}} of {{max}}',
    emails: 'Emails',
    noEmail: 'No email.',
    nameExampleCom: 'name@example.com',
    emailV: 'Email {{v}}',
    typeOfEmailV: 'Type of email {{v}}',
    primary: 'Primary',
    deleteEmailV: 'Delete email {{v}}',
    invalidEmailFormat: 'Invalid email format.',
    addAnEmail: '+ Add an email',
    phones: 'Phones',
    noPhone: 'No phone.',
    phoneV: 'Phone {{v}}',
    typeOfPhoneV: 'Type of phone {{v}}',
    deletePhoneV: 'Delete phone {{v}}',
    addAPhone: '+ Add a phone',
    socialNetworks: 'Social networks',
    noNetwork: 'No network.',
    network: 'network',
    networkV: 'Network {{v}}',
    handleOrUrl: '@handle or URL',
    handleV: 'Handle {{v}}',
    deleteNetworkV: 'Delete network {{v}}',
    addANetwork: '+ Add a network',
    entityType: 'Entity type',
    address: 'Address',
    n12ExampleStreet: '12 Example Street',
    postalCode: 'Postal code',
    city: 'City',
    regionState: 'Region / State',
    country: 'Country',
    customFields: 'Custom fields',
    note: 'Note',
    addALabelEnter: 'Add a label + Enter',
    removeT: 'Remove {{t}}',
    addALabel: 'Add a label',
    street: 'Street',
    region: 'Region',
    amount: 'Amount',
    cad: 'CAD',
    currency: 'Currency',
    https: 'https://…',
    otherCustomFields: 'Other custom fields',
    noAdditionalField: 'No additional field.',
    fieldName: 'field name',
    customFieldNameV: 'Custom field name {{v}}',
    value: 'value',
    customFieldValueV: 'Custom field value {{v}}',
    deleteFieldV: 'Delete field {{v}}',
    trimmedMatchesAPredefinedFieldEdit: '“{{trimmed}}” matches a predefined field — edit it in the section above. This value will be ignored.',
    thisFieldNameIsAlreadyIn: 'This field name is already in use.',
    addACustomField: '+ Add a custom field',
  },
  contactFormModal: {
    enterAtLeastANameOr: 'Enter at least a name or an email.',
    fixTheEmailsWithAnInvalid: 'Fix the emails with an invalid format before saving.',
    aContactWithThisEmailAlready: 'A contact with this email already exists.',
    theUpdateFailedTryAgain: 'The update failed. Try again.',
    theCreationFailedTryAgain: 'The creation failed. Try again.',
    editTheContact: 'Edit the contact',
    newContact: 'New contact',
    cancel: 'Cancel',
    saving: 'Saving…',
    save: 'Save',
    createTheContact: 'Create the contact',
    aContactWithThisEmailAlready2: 'A contact with this email already exists. You can fill in its empty fields with this information or open the existing record.',
    updating: 'Updating…',
    updateTheExistingContact: 'Update the existing contact',
    openTheExistingContact: 'Open the existing contact',
    advancedFields: 'Advanced fields',
    identity: 'Identity',
    firstName: 'First name',
    name: 'Name',
    entityType: 'Entity type',
    contactDetails: 'Contact details',
    address: 'Address',
    professional: 'Professional',
    company: 'Company',
    jobTitle: 'Job title',
    segmentation: 'Segmentation',
    lifecycleStage: 'Lifecycle stage',
    owner: 'Owner',
    leadSource: 'Lead source',
    note: 'Note',
    labels: 'Labels',
    socialNetworks: 'Social networks',
    addressCustomField: 'Address (custom field)',
    customFields: 'Custom fields',
    otherCustomFields: 'Other custom fields',
    consentGdpr: 'Consent (GDPR)',
    doNotContactOptOutSuppression: 'Do not contact (opt-out / suppression list)',
    checkIfThePersonAskedNot: 'Check if the person asked not to be contacted. Sends respect this choice.',
  },
  contactPicker: {
    linkedContact: 'Linked contact',
    remove: 'Remove',
    searchForAContactNameEmail: 'Search for a contact (name, email)…',
    searching: 'Searching…',
    noContactFound: 'No contact found.',
  },
  contactsPage: {
    filterByEntityType: 'Filter by entity type',
    firstName: 'First name',
    name: 'Name',
    email: 'Email',
    theBulkActionFailed: 'The bulk action failed.',
    selectionLimitedToTheFirstSelected: 'Selection limited to the first {{selected}} contacts ',
    ofMatchedMatching: '(of {{matched}} matching).',
    selectedContactSMatchingTheFilter: '{{selected}} contact(s) matching the filter selected.',
    couldNotSelectAllTheContacts: 'Could not select all the contacts matching the filter.',
    contactAddedToTheSelectionChoose: 'Contact added to the selection — choose a group in the action bar.',
    contact: 'Contact',
    type: 'Type',
    company: 'Company',
    score: 'Score',
    lastInteraction: 'Last interaction',
    vInteractionS: '{{v}} interaction(s)',
    created: 'Created',
    findEnrichEmailPhoneNetworksAddress: 'Find & Enrich (email, phone, networks, address)',
    enrich: '✨ Enrich',
    selectV: 'Select {{v}}',
    analysisRecommendationsProspecting: 'Analysis & recommendations — prospecting',
    describeWhoYouAreLookingFor: 'Describe who you are looking for… (e.g. clients in Paris with no recent activity)',
    naturalLanguageSearch: 'Natural-language search',
    search: 'Search',
    searchForAContactNameEmail: 'Search for a contact (name, email)…',
    searchForAContact: 'Search for a contact',
    naturalLanguageSearchAi: 'Natural-language search (AI)',
    ai: 'AI',
    filterByStage: 'Filter by stage',
    allStages: 'All stages',
    filterByGroup: 'Filter by group',
    allGroups: 'All groups',
    filterByOwner: 'Filter by owner',
    postalCode: 'Postal code',
    filterByPostalCode: 'Filter by postal code',
    city: 'City',
    filterByCity: 'Filter by city',
    region: 'Region',
    filterByRegion: 'Filter by region',
    filterByCountry: 'Filter by country',
    searchAroundAPointLatitudeLongitude: 'Search around a point (latitude, longitude + radius)',
    latitude: 'Latitude',
    latitudeOfTheSearchPoint: 'Latitude of the search point',
    longitude: 'Longitude',
    longitudeOfTheSearchPoint: 'Longitude of the search point',
    radiusKm: 'Radius (km)',
    enterLatitudeAndLongitudeToEnable: 'Enter latitude and longitude to enable the radius.',
    searchRadiusInKilometers: 'Search radius in kilometers',
    myAssignedLeads: 'My assigned leads',
    teamLeads: 'Team leads',
    contactsVisibleThanksToAShare: 'Contacts visible thanks to a share (not owned by you).',
    sharedWithMe: 'Shared with me',
    advancedFilters: 'Advanced filters',
    export: 'Export',
    import: 'Import',
    pasteAListOfEmailsAnd: 'Paste a list of emails and preview duplicates before adding',
    addByEmail: 'Add by email',
    newContact: '+ New contact',
    aiCriteria: 'AI criteria:',
    reset: 'Reset',
    the: 'The',
    contactsMatchingTheFilterAreSelected: 'contacts matching the filter are selected.',
    theLengthContactsOnThisPage: 'The {{length}} contacts on this page are selected.',
    selecting: 'Selecting…',
    selectTheTotalContactsMatchingThe: 'Select the {{total}} contacts matching the filter',
    selected: 'selected',
    targetGroup: 'Target group',
    group: 'Group…',
    addToGroup: 'Add to group',
    createANewGroupAndAdd: 'Create a new group and add the selection to it',
    newGroup: '+ New group',
    ownerToAssign: 'Owner to assign',
    assign: 'Assign',
    stageToApply: 'Stage to apply',
    changeTheStage: 'Change the stage',
    findEnrichTheSelectedContacts: 'Find & Enrich the selected contacts',
    enrichTheSelection: '✨ Enrich the selection',
    adminOrOwnerRoleRequiredTo: 'Admin or owner role required to share.',
    shareTheSelectedContactsWithOne: 'Share the selected contacts with one or more members',
    shareTheSelection: '🤝 Share the selection',
    delete: 'Delete',
    clearTheSelection: 'Clear the selection',
    loading: 'Loading…',
    noContactSharedWithYouYet: 'No contact shared with you yet.',
    noContactMatchesTheFilters: 'No contact matches the filters.',
    couldNotOpenTheExistingContact: 'Could not open the existing contact.',
    deleteTheContacts: 'Delete the contacts',
    deleteLengthContactSThisCannot: 'Delete {{length}} contact(s)? This cannot be undone.',
    newGroup2: 'New group',
    groupName: 'Group name',
    eGQ3Prospects: 'E.g. Q3 prospects',
    createAndAdd: 'Create and add',
    stage: 'Stage',
    owner: 'Owner',
    enrich2: 'Enrich',
    text: 'Text',
  },
  conversationIntelligence: {
    summary: 'Summary',
    meddic: 'MEDDIC',
    bant: 'BANT',
    metrics: 'Metrics',
    economicBuyer: 'Economic buyer',
    decisionCriteria: 'Decision criteria',
    decisionProcess: 'Decision process',
    identifiedPain: 'Identified pain',
    champion: 'Champion',
    budget: 'Budget',
    authority: 'Authority',
    need: 'Need',
    timeline: 'Timeline',
    positive: 'Positive',
    neutral: 'Neutral',
    negative: 'Negative',
    queued: 'Queued',
    inProgress: 'In progress',
    awaitingValidation: 'Awaiting validation',
    paused: 'Paused',
    done: 'Done',
    failed: 'Failed',
    cancelled: 'Cancelled',
    conversationAnalysisFailed: 'Conversation analysis failed.',
    launchingTheSdrAutopilotFailed: 'Launching the SDR autopilot failed.',
    intelligence: 'Intelligence',
    analyzeAConversation: 'Analyze a conversation',
    pasteTheCallTranscriptOrYour: 'Paste the call transcript or your meeting notes…',
    conversationTranscript: 'Conversation transcript',
    framework: 'Framework:',
    analyzing: 'Analyzing…',
    analyze: 'Analyze',
    actionItems: 'Action items',
    pinnedNote: 'Pinned note',
    followUp: ' + follow-up',
    added: 'added',
    toTheDeal: 'to the deal',
    toTheContact: 'to the contact',
    sdrAutopilot: 'SDR autopilot',
    goalEGBookADemo: 'Goal (e.g. “book a demo meeting”)',
    sdrAutopilotGoal: 'SDR autopilot goal',
    readOnly: 'Read-only',
    launching: 'Launching…',
    startSdrAutopilot: 'Start SDR autopilot',
    run: 'Run',
    viewInAiActivity: 'View in AI Activity',
    linkAContactToThisDeal: 'Link a contact to this deal to start an SDR autopilot sequence.',
  },
  crmLayout: {
    data: 'Data',
    sales: 'Sales',
    tools: 'Tools',
    admin: 'Admin',
    crmProspecting: 'CRM & Prospecting',
    contactsCompaniesSegmentsAndSalesPipelin: 'Contacts, companies, segments and sales pipeline.',
    crmNavigation: 'CRM navigation',
  },
  crmSettingsPage: {
    keyAndLabelAreRequired: 'Key and label are required.',
    anAiFieldNeedsAPrompt: 'An AI field needs a prompt.',
    thisKeyAlreadyExistsForThis: 'This key already exists for this type.',
    couldNotCreate: 'Could not create.',
    newCustomField: 'New custom field',
    cancel: 'Cancel',
    adding: 'Adding…',
    addTheField: 'Add the field',
    entity: 'Entity',
    type: 'Type',
    key: 'Key',
    linkedinUrl: 'linkedin_url',
    label: 'Label',
    optionsCsv: 'Options (csv)',
    aiPrompt: 'AI prompt',
    useLabelToReferenceAField: 'Use {Label} to reference a field: {{v}}',
    useLabelToInsertTheValue: 'Use {Label} to insert the value of another field of the entity.',
    eGSummarizeInOneSentence: 'E.g. Summarize in one sentence the line of business of {Company} and its potential.',
    selectAnAiFieldAndA: 'Select an AI field and a group.',
    bulkComputationFailedAiUnavailable: 'Bulk computation failed (AI unavailable?).',
    computeAnAiFieldForA: 'Compute an AI field for a group',
    close: 'Close',
    computationInProgress: 'Computation in progress…',
    compute: 'Compute',
    contactSUpdated: 'contact(s) updated.',
    aiField: 'AI field',
    group: 'Group',
    chooseAGroup: '— Choose a group —',
    couldNotLoadTheCustomFields: 'Could not load the custom fields.',
    options: 'Options',
    actions: 'Actions',
    delete: 'Delete',
    customFields: 'Custom fields',
    additionalAttributesTextAddressNoteLabel: 'Additional attributes (text, address, note, labels, magic AI…) for contacts, companies and deals.',
    computeForTheGroup: 'Compute for the group',
    newField: '+ New field',
    noCustomFields: 'No custom fields.',
    loading: 'Loading…',
    deleteTheField: 'Delete the field',
    deleteTheFieldLabel: 'Delete the field “{{label}}”?',
    theNameIsRequired: 'The name is required.',
    couldNotCreateTheRule: 'Could not create the rule.',
    newAssignmentRule: 'New assignment rule',
    addTheRule: 'Add the rule',
    name: 'Name',
    strategy: 'Strategy',
    membersIdsCsv: 'Members (IDs, csv)',
    user1User2: 'user-1, user-2',
    noContactToDistributeForThis: 'No contact to distribute for this scope.',
    couldNotDistribute: 'Could not distribute.',
    runName: 'Run “{{name}}”',
    distributing: 'Distributing…',
    distribute: 'Distribute',
    contactSAssignedAccordingToThe: 'contact(s) assigned according to the rule.',
    scope: 'Scope',
    contactsWithoutAnOwner: 'Contacts without an owner',
    contactsInAGroup: 'Contacts in a group',
    couldNotUpdate: 'Could not update.',
    members: 'Members',
    active: 'Active',
    inactive: 'Inactive',
    view: 'View',
    distributeContactsAccordingToThisRule: 'Distribute contacts according to this rule',
    inactiveRule: 'Inactive rule',
    runTheRule: 'Run the rule',
    assignmentRules: 'Assignment rules',
    leadDistributionRoundRobinOverA: 'Lead distribution (round-robin over a list of members).',
    newRule: '+ New rule',
    noRules: 'No rules.',
    deleteTheRule: 'Delete the rule',
    deleteTheRuleName: 'Delete the rule “{{name}}”?',
    emailDomain: 'Email domain',
    hunterIo: 'Hunter.io',
    apolloIo: 'Apollo.io',
    peopleDataLabs: 'People Data Labs',
    reservedForTheAccountOwnerOr: 'Reserved for the account owner or administrator (Owner/Admin only): provider API keys are account secrets.',
    configured: 'Configured ·',
    notConfigured: 'not configured',
    addAtLeastOneProviderTo: 'Add at least one provider to the cascade.',
    couldNotSave: 'Could not save.',
    editTheConfiguration: 'Edit the configuration',
    newEnrichmentConfiguration: 'New enrichment configuration',
    saving: 'Saving…',
    save: 'Save',
    createTheConfiguration: 'Create the configuration',
    contactCascade: 'Contact cascade',
    theEntityTypeCannotBeChanged: 'The entity type cannot be changed.',
    contacts: 'Contacts',
    companies: 'Companies',
    activation: 'Activation',
    configurationActive: 'Configuration active',
    configurationDisabled: 'Configuration disabled',
    providerCascadeLength: 'Provider cascade ({{length}})',
    triedInOrderFromTopTo: 'Tried in order from top to bottom; the first one that returns data wins.',
    noProviderAddAtLeastOne: 'No provider — add at least one below.',
    apiKeyRequired: 'API key required',
    noKeyFree: 'No key (free)',
    unknownProvider: ' · unknown provider',
    moveUp: 'Move up',
    moveDown: 'Move down',
    removeFromTheCascade: 'Remove from the cascade',
    remove: 'Remove',
    keySaved: '•••••••• (key saved)',
    apiKey: 'API key',
    theKeyWillBeClearedOn: 'The key will be cleared on save.',
    clearTheKey: 'Clear the key',
    availableProviders: 'Available providers',
    couldNotLoadTheEnrichmentConfigurations: 'Could not load the enrichment configurations.',
    couldNotDelete: 'Could not delete.',
    cascade: 'Cascade',
    apiKeys: 'API keys',
    edit: 'Edit',
    enrichment: 'Enrichment',
    enrichmentProviderCascadeOrderPerProvide: 'Enrichment provider cascade (order, per-provider API keys, activation). Providers are tried in order: the first one that returns data wins.',
    newConfiguration: '+ New configuration',
    providerApiKeysAreManagedBy: 'Provider API keys are managed by the account owner or administrator (Owner/Admin only). They are masked for everyone.',
    noEnrichmentConfiguration: 'No enrichment configuration.',
    deleteTheConfiguration: 'Delete the configuration',
    deleteTheConfigurationName: 'Delete the configuration “{{name}}”?',
    inProgress: 'In progress',
    won: 'Won',
    lost: 'Lost',
    theStageNameIsRequired: 'The stage name is required.',
    editTheStage: 'Edit the stage',
    newJourneyStage: 'New journey stage',
    deleting: 'Deleting…',
    confirmDeletion: 'Confirm deletion',
    add: 'Add',
    deleteTheStageNameTheChecklist: 'Delete the stage “{{name}}”? The checklist templates attached to this stage will also be removed. A stage that still holds contacts cannot be deleted.',
    qualification: 'Qualification',
    color: 'Color',
    colorPicker: 'Color picker',
    hexadecimalColorCode: 'Hexadecimal color code',
    stageType: 'Stage type',
    wonLostMarkTheTerminalStages: '“Won” / “Lost” mark the terminal stages of the journey.',
    description: 'Description',
    stageGoalEntryExitCriteria: 'Stage goal, entry / exit criteria…',
    couldNotLoadTheJourneyStages: 'Could not load the journey stages.',
    couldNotReorder: 'Could not reorder.',
    leadJourneyStages: 'Lead journey stages',
    theEngagementFunnelJourneyYourLeads: 'The engagement funnel (Journey) your leads move through: add, rename, recolor, reorder or delete stages. “In progress / Won / Lost” qualify the terminal stages.',
    newStage: '+ New stage',
    noStageDefined: 'No stage defined.',
    moveNameUp: 'Move {{name}} up',
    moveNameDown: 'Move {{name}} down',
    couldNotCreateTheToken: 'Could not create the token.',
    newCaptureToken: 'New capture token',
    creating: 'Creating…',
    createTheToken: 'Create the token',
    forYourOwnReferenceEG: 'For your own reference (e.g. “Website form”, “Webinar landing page”).',
    websiteForm: 'Website form',
    defaultGroup: 'Default group',
    contactsCapturedThroughThisTokenWill: 'Contacts captured through this token will join this group (optional).',
    none: '— None —',
    aTokenCreatesAPublicUrl: 'A token creates a public URL (no authentication): every request',
    post: 'POST',
    ofAJsonBodyToThis: 'of a JSON body to this URL creates a contact. Recognized keys (first_name, last_name, email, phone, job_title…) are mapped automatically; the others become custom fields.',
    unlabeledToken: 'Unlabeled token',
    createdOnV: 'Created on {{v}}',
    copyThePublicUrl: 'Copy the public URL',
    copied: 'Copied!',
    copy: 'Copy',
    couldNotLoadTheCaptureTokens: 'Could not load the capture tokens.',
    captureWebhooks: 'Capture / Webhooks',
    publicLeadCaptureTokensEachToken: 'Public lead-capture tokens: each token exposes an unauthenticated URL to plug into an external form or automation; a POST request to it creates a contact in your CRM.',
    newToken: '+ New token',
    noCaptureTokensCreateOneTo: 'No capture tokens. Create one to receive leads from an external form.',
    deleteTheToken: 'Delete the token',
    deleteTheTokenVItsPublic: 'Delete the token “{{v}}”? Its public URL will stop working immediately.',
    configurationHealth: 'Configuration & health',
    overviewOfYourCrmDataModel: 'Overview of your CRM data model and lead distribution.',
    length3EntitiesCovered: '{{length}}/3 entities covered',
    fieldTypes: 'Field types',
    typesUsed: 'types used',
    entitiesCovered: 'Entities covered',
    missingV: 'missing: {{v}}',
    contactCompanyDeal: 'contact · company · deal',
    fieldsPerEntity: 'Fields per entity',
    unavailable: 'unavailable',
    typeBreakdown: 'Type breakdown',
    configurationInsights: 'Configuration insights',
    entityCoverage: 'Entity coverage',
    entitiesWithoutFields: 'Entities without fields',
    activeRules: 'Active rules',
    disabledRules: 'Disabled rules',
    equals: 'equals',
    notEqualTo: 'not equal to',
    notIn: 'not in',
    isSet: 'is set',
    recomputeScores: 'Recompute scores',
    recomputing: 'Recomputing…',
    recomputeDone: '{{processed}} contacts recomputed, {{nonzero}} with a score above 0.',
    recomputeFailed: 'Could not recompute the scores.',
    defaultIcpBanner:
      'You are using the default ICP profile (seniority, e-mail, phone, enrichment, company…) and the engagement rules below. Adjust the points and save to make it your own profile; scores are recomputed automatically.',
    engagementRules: 'Engagement rules (real CRM data)',
    engagementRulesHint:
      'Points per event and cap: e-mails exchanged, notes, calls, meetings, bookings, forms, deals and recent interaction.',
    pointsPerEvent: 'Points per event',
    cap: 'Cap',
    websiteVisit: 'Website visit',
    pricingPageView: 'Pricing page view',
    demoRequest: 'Demo request',
    contentDownload: 'Content download',
    webinarAttendance: 'Webinar attendance',
    competitiveResearch: 'Competitive research',
    g2CategoryView: 'G2 category view',
    repeatVisit: 'Repeat visit',
    highIntentKeyword: 'High-intent keyword',
    emailOpened: 'Email opened',
    emailClicked: 'Email clicked',
    emailReplied: 'Email replied',
    socialInteraction: 'Social interaction',
    noDefaultWeightProvided: 'No default weight provided.',
    defaultDef: '(default {{def}}',
    modified: ' · modified',
    weightForV: 'Weight for {{v}}',
    scoringConfiguration: 'Scoring configuration',
    couldNotLoadTheScoringConfiguration: 'Could not load the scoring configuration.',
    couldNotSaveCheckYourPermissions: 'Could not save. Check your permissions and try again.',
    leadScoringIdealCustomerProfileIcp: 'Lead scoring & ideal customer profile (ICP)',
    adjustTheWeightOfIntentSignals: 'Adjust the weight of intent signals and activities, and define your ICP criteria. Without configuration, scoring uses the platform defaults.',
    resetTheWeightsToThePlatform: 'Reset the weights to the platform defaults',
    resetTheWeights: 'Reset the weights',
    configurationSaved: 'Configuration saved ✓',
    scoringEngineUnavailableForThisAccount: 'Scoring engine unavailable for this account.',
    tryAgain: 'Try again',
    configurationName: 'Configuration name',
    scoringEnabled: 'Scoring enabled',
    intentSignalWeights: 'Intent signal weights',
    activityWeights: 'Activity weights',
    idealCustomerProfileIcpCriteria: 'Ideal customer profile (ICP) criteria',
    addACriterion: '+ Add a criterion',
    noIcpCriteriaAddSomeTo: 'No ICP criteria. Add some to score fit (industry, size, country, stage, custom field…).',
    field: 'Field',
    industryCountryCustomKey: 'industry, country, custom.<key>…',
    operator: 'Operator',
    value: 'Value',
    noValueRequired: 'No value required',
    valuesCommaSeparated: 'Values (comma-separated)',
    eGFrCaUs: 'e.g. FR, CA, US',
    points: 'Points',
    deleteTheCriterion: 'Delete the criterion',
    maximumIcpPointsOptional: 'Maximum ICP points (optional)',
    capUsedToNormalizeTheFit: 'Cap used to normalize the fit score. Leave empty for the sum of the criteria.',
    unlabeled: 'unlabeled',
    company: 'Company',
    deals: 'Deals',
    contact: 'Contact',
  },
  dashboardsPage: {
    customShort: 'Custom',
    contactsCreated: 'Contacts created',
    contactsPerStage: 'Contacts per stage',
    contactSources: 'Contact sources',
    teamLeaderboardDealsWon: 'Team leaderboard — deals won',
    removeTitle: 'Remove {{title}}',
    dataUnavailable: 'Data unavailable.',
    loading: 'Loading…',
    viewContacts: 'View contacts',
    noData: 'No data.',
    newWidget: 'New widget',
    addAWidget: 'Add a widget',
    cancel: 'Cancel',
    add: 'Add',
    type: 'Type',
    title: 'Title',
    eGDealsByStatus: 'E.g. Deals by status',
    entity: 'Entity',
    contacts: 'Contacts',
    companies: 'Companies',
    deals: 'Deals',
    measure: 'Measure',
    count: 'Count',
    sumOfAField: 'Sum of a field',
    fieldToSum: 'Field to sum',
    value: 'value',
    groupBy: 'Group by',
    stage: 'Stage',
    owner: 'Owner',
    source: 'Source',
    status: 'Status',
    pipelineStage: 'Pipeline stage',
    metric: 'Metric',
    dealsWon: 'Deals won',
    activities: 'Activities',
    dealValue: 'Deal value',
    contactsAdded: 'Contacts added',
    velocityUnavailable: 'Velocity unavailable.',
    pipelineVelocity: 'Pipeline velocity',
    computing: 'Computing…',
    averageCycle: 'Average cycle',
    stalledDeals: 'Stalled deals',
    openDealsOpen: '· {{open_deals}} open',
    aiAnalysisUnavailableAiOffline: 'AI analysis unavailable (AI offline?).',
    pipelineHealthAi: 'Pipeline health (AI)',
    analyzing: 'Analyzing…',
    reanalyze: 'Reanalyze',
    analyze: 'Analyze',
    runAnAiAnalysisToAssess: 'Run an AI analysis to assess pipeline health, at-risk deals and bottlenecks.',
    health: 'Health',
    atRiskDeals: 'At-risk deals',
    bottlenecks: 'Bottlenecks',
    recommendations: 'Recommendations',
    summaryUnavailable: 'Summary unavailable.',
    couldNotSaveTheDashboard: 'Could not save the dashboard.',
    couldNotDelete: 'Could not delete.',
    openDeals: 'Open deals',
    winRate: 'Win rate',
    averageValue: 'Average value',
    silentContacts: 'Silent contacts',
    new7d: 'New (7d)',
    remindersDue: 'Reminders due',
    numberOfDays: 'Number of days',
    filterByOwner: 'Filter by owner',
    pipeline: 'Pipeline',
    allPipelines: 'All pipelines',
    realTimeFeedActive: 'Real-time feed active',
    realTimeUnavailablePointInTime: 'Real time unavailable — point-in-time data',
    live: 'Live',
    offline: 'Offline',
    loadingIndicators: 'Loading indicators…',
    dashboard: 'Dashboard:',
    loadADashboard: 'Load a dashboard',
    default: 'Default',
    widget: '+ Widget',
    save: 'Save',
    delete: 'Delete',
    pipelineFunnel: 'Pipeline funnel',
    engagementMapDayHour: 'Engagement map (day × hour)',
    saveTheDashboard: 'Save the dashboard',
    name: 'Name',
    eGTeamPerformance: 'E.g. Team performance',
    unsavedChanges: 'Unsaved changes',
    saving: 'Saving…',
    saveAs: 'Save as…',
    dashboardNameCreated: 'Dashboard “{{name}}” created.',
    dashboardNameSaved: 'Dashboard “{{name}}” saved.',
  },
  documentsPage: {
    documentsAndQuotes: 'Documents & quotes',
    theTitleIsRequired: 'The title is required.',
    addAtLeastOneLine: 'Add at least one line.',
    couldNotSave: 'Could not save.',
    editTheDocument: 'Edit the document',
    newDocument: 'New document',
    cancel: 'Cancel',
    saving: 'Saving…',
    save: 'Save',
    type: 'Type',
    title: 'Title',
    quoteWebsiteRedesign: 'Quote — Website redesign',
    contactRecipient: 'Contact / recipient',
    documentRecipient: 'Document recipient',
    rows: 'Rows',
    addALine: '+ Add a line',
    description: 'Description',
    quantity: 'Quantity',
    unitPrice: 'Unit price',
    taxRate: 'Tax rate (%)',
    currency: 'Currency',
    discountAmount: 'Discount (amount)',
    subtotal: 'Subtotal',
    tax: 'Tax',
    discount: 'Discount',
    total: 'Total',
    notes: 'Notes',
    termsLegalNotices: 'Terms, legal notices…',
    couldNotLoadTheDocuments: 'Could not load the documents.',
    couldNotSend: 'Could not send.',
    quotesAndProposalsWithLineItems: 'Quotes and proposals with line items, totals and online signature.',
    all: 'All',
    noDocumentsCreateAQuoteOr: 'No documents. Create a quote or a proposal to send.',
    createADocument: 'Create a document',
    signatureLink: 'Signature link',
    sending: 'Sending…',
    send: 'Send',
    edit: 'Edit',
    delete: 'Delete',
    deleteTheDocument: 'Delete the document?',
    titleWillBePermanentlyDeleted: '“{{title}}” will be permanently deleted.',
  },
  duplicatesPage: {
    duplicateDetectionUnavailable: 'Duplicate detection unavailable.',
    couldNotMergeTryAgain: 'Could not merge. Try again.',
    similarityThreshold: 'Similarity threshold',
    thresholdInput: 'Threshold (input)',
    analyze: 'Analyze',
    searchingForDuplicates: 'Searching for duplicates…',
    noDuplicateDetectedAtTheCurrent: 'No duplicate detected at the current threshold. Lower the threshold to broaden.',
    potentialDuplicates: 'potential duplicates',
    similarity: '% similarity',
    keep: 'Keep',
    mergeIntoTheKeptContact: 'Merge into the kept contact',
    mergeTheDuplicates: 'Merge the duplicates',
    mergeTheOtherContactsIntoThe: 'Merge the other contacts into the kept contact? This cannot be undone.',
    merge: 'Merge',
  },
  exportModal: {
    selectAtLeastOneColumn: 'Select at least one column.',
    exportComplete: 'export complete',
    downloadedFilenameRows: 'Downloaded: {{filename}} — {{rows}}.',
    couldNotExport: 'Could not export.',
    close: 'Close',
    cancel: 'Cancel',
    exporting: 'Exporting…',
    export: 'Export',
    exportTheEntitylabel: 'Export the {{entityLabel}}',
    format: 'Format',
    scope: 'Scope',
    allEntitylabel: 'All ({{entityLabel}})',
    currentFilter: 'Current filter',
    selectionLength: 'Selection ({{length}})',
    theFileWillContainEstimateRow: 'The file will contain {{estimate}} row(s).',
    theFileWillContainAllMatching: 'The file will contain all matching rows.',
    chooseTheColumns: 'Choose the columns',
    defaultSet: '(default set)',
    all: 'All',
    default: 'Default',
    none: 'None',
  },
  followUpsPage: {
    aiDraftUnavailableRightNow: 'AI draft unavailable right now.',
    writeAFollowUpV: 'Write a follow-up — {{v}}',
    copied: 'Copied ✓',
    copy: 'Copy',
    writing: 'Writing…',
    subject: 'Subject',
    message: 'Message',
    editableCopyItToYourComposer: 'Editable — copy it to your composer or the email service.',
    couldNotLoadTheContactsTo: 'Could not load the contacts to follow up.',
    noActivityFor: 'No activity for',
    customNumberOfDays: 'Custom number of days',
    myContacts: 'My contacts',
    refresh: 'Refresh',
    noSilentContactsInThisPeriod: 'No silent contacts in this period. Nice work!',
    daysQuietDOfSilence: '{{days_quiet}} d of silence',
    never: 'Never',
    writeAFollowUp: 'Write a follow-up',
  },
  forecastGoals: {
    forecastsUnavailableEngineNotDeployed: 'Forecasts unavailable (engine not deployed?).',
    weighted: 'Weighted',
    bestCase: 'Best case',
    committed: 'Committed',
    won: 'Won',
    projection: 'Projection',
    revenueForecasts: 'Revenue forecasts',
    pipelineWeightedByWinProbabilityTrend: 'Pipeline weighted by win probability + trend projection.',
    openDealS: 'open deal(s)',
    computingForecasts: 'Computing forecasts…',
    noForecastData: 'No forecast data.',
    weightedValueByStage: 'Weighted value by stage',
    noOpenDeals: 'No open deals.',
    cumulativeProjection: 'Cumulative projection',
    couldNotSaveTheObjective: 'Could not save the objective.',
    editTheObjective: 'Edit the objective',
    newObjective: 'New objective',
    cancel: 'Cancel',
    saving: 'Saving…',
    save: 'Save',
    create: 'Create',
    name: 'Name',
    eGQ3Revenue: 'E.g. Q3 revenue',
    metric: 'Metric',
    targetAmount: 'Target (amount)',
    targetCount: 'Target (count)',
    period: 'Period',
    pipelineOptional: 'Pipeline (optional)',
    allPipelines: 'All pipelines',
    ownerOptional: 'Owner (optional)',
    objectiveOwner: 'Objective owner',
    startOptional: 'Start (optional)',
    endOptional: 'End (optional)',
    achieved: 'Achieved',
    onTrack: 'On track',
    behind: 'Behind',
    editName: 'Edit {{name}}',
    edit: 'Edit',
    deleteName: 'Delete {{name}}',
    delete: 'Delete',
    progressName: 'Progress — {{name}}',
    expectedPaceV: 'Expected pace: {{v}}',
    endOfPeriodProjection: 'End-of-period projection:',
    objectivesUnavailableEngineNotDeployed: 'Objectives unavailable (engine not deployed?).',
    objectives: 'Objectives',
    progressTrackingPaceVersusTheTarget: 'Progress tracking + pace versus the target.',
    readOnly: 'Read-only',
    objective: '+ Objective',
    loadingObjectives: 'Loading objectives…',
    noObjectiveDefined: 'No objective defined.',
    createAnObjective: 'Create an objective',
    deleteTheObjective: 'Delete the objective',
    deleteNameThisActionIsPermanent: 'Delete “{{name}}”? This action is permanent.',
  },
  format: {
    justNow: 'just now',
    sSAgo: '{{s}} s ago',
    mMinAgo: '{{m}} min ago',
    hHAgo: '{{h}} h ago',
    vDAgo: '{{v}} d ago',
    unnamedContact: 'Unnamed contact',
    lead: 'Lead',
    person: 'Person',
    group: 'Group',
    media: 'Media',
    organization: 'Organization',
    company: 'Company',
    agency: 'Agency',
    government: 'Government',
    program: 'Program',
    other: 'Other',
    open: 'Open',
    won: 'Won',
    lost: 'Lost',
    toDo: 'To do',
    done: 'Done',
    postponed: 'Postponed',
    merge: 'Merge',
    assignment: 'Assignment',
    stageChange: 'Stage change',
    import: 'Import',
    emailOpened: 'Email opened',
    emailClicked: 'Email clicked',
    emailReply: 'Email reply',
    socialInteraction: 'Social interaction',
    prospect: 'Prospect',
    client: 'Client',
    influencer: 'Influencer',
    partner: 'Partner',
  },
  groupsPage: {
    memberCountOne: '{{count}} member',
    memberCountMany: '{{count}} members',
    theNameIsRequired: 'The name is required.',
    couldNotSave: 'Could not save.',
    editTheGroup: 'Edit the group',
    newGroup: 'New group',
    cancel: 'Cancel',
    saving: 'Saving…',
    save: 'Save',
    emoji: 'Emoji',
    name: 'Name',
    description: 'Description',
    color: 'Color',
    colorC: 'Color {{c}}',
    searchForAContactToAdd: 'Search for a contact to add…',
    searchForAContactToAdd2: 'Search for a contact to add',
    search: 'Search',
    add: 'Add',
    couldNotLoadTheMembers: 'Could not load the members.',
    couldNotRemove: 'Could not remove.',
    segments: 'Segments',
    groupYourContactsIntoTargetedLists: 'Group your contacts into targeted lists and track their reach.',
    newGroup2: '+ New group',
    nonemptyActiveEmptyEmpty: '{{nonEmpty}} active · {{empty}} empty',
    loading: 'loading…',
    segmentedMembers: 'Segmented members',
    allMemberships: 'all memberships',
    averageSize: 'Average size',
    membersSegment: 'members / segment',
    crmContacts: 'CRM contacts',
    newThisWeekThisWeek: '+{{new_this_week}} this week',
    memberBreakdown: 'Member breakdown',
    unavailable: 'unavailable',
    noMemberships: 'No memberships.',
    lifecycleMix: 'Lifecycle mix',
    noContacts: 'No contacts.',
    segmentInsights: 'Segment insights',
    segmentation: 'Segmentation',
    membershipsContacts: 'memberships / contacts',
    largestName: 'Largest · {{name}}',
    emptySegments: 'Empty segments',
    followUpsDue: 'Follow-ups due',
    noGroups: 'No groups.',
    loading2: 'Loading…',
    manageTheMembersOfName: 'Manage the members of {{name}}',
    edit: 'Edit',
    delete: 'Delete',
    adminOrOwnerRoleRequiredTo: 'Admin or owner role required to share.',
    share: 'Share',
    members: 'Members',
    memberS: 'member(s)',
    averageScore: 'Average score',
    leadScore: 'lead score',
    dominantStage: 'Dominant stage',
    steps: 'Steps',
    lifecycle: 'lifecycle',
    memberLifecycle: 'Member lifecycle',
    linkedProjectsLength: 'Linked projects ({{length}})',
    noMembersInThisGroup: 'No members in this group.',
    remove: 'Remove',
    deleteTheGroup: 'Delete the group',
    deleteTheGroupNameThisCannot: 'Delete the group “{{name}}”? This cannot be undone.',
  },
  growthShared: {
    featureUnavailable: '{{feature}} — unavailable',
    theServiceIsNotDeployedOn: 'The service is not deployed on the server yet. The page will work as soon as it responds.',
    copyLink: 'Copy link',
    copied: 'Copied!',
  },
  importWizardModal: {
    customFieldLabel: 'Custom field: {{label}}',
    email: 'Email',
    phone: 'Phone',
    firstName: 'First name',
    name: 'Name',
    jobTitle: 'Job title',
    companyId: 'Company (ID)',
    ownerId: 'Owner (ID)',
    stage: 'Stage',
    entityType: 'Entity type',
    source: 'Source',
    avatarUrl: 'Avatar (URL)',
    address: 'Address',
    postalCode: 'Postal code',
    city: 'City',
    region: 'Region',
    countryIso2: 'Country (ISO-2)',
    latitude: 'Latitude',
    longitude: 'Longitude',
    couldNotDownloadTheTemplate: 'Could not download the template.',
    couldNotImportTheFile: 'Could not import the file.',
    mapAtLeastOneColumnTo: 'Map at least one column to “Email” (required for deduplication).',
    couldNotPreview: 'Could not preview.',
    couldNotImport: 'Could not import.',
    close: 'Close',
    cancel: 'Cancel',
    sending: 'Sending…',
    analyzeTheFile: 'Analyze the file',
    analyzing: 'Analyzing…',
    preview: 'Preview',
    importing: 'Importing…',
    import: 'Import',
    importContactsCsvXlsxJsonTsv: 'Import contacts (CSV / XLSX / JSON / TSV)',
    file: 'File',
    columns: 'Columns',
    preview2: 'Preview',
    done: 'Done',
    aCsvXlsxJsonOrTsv: 'A .csv, .xlsx, .json or .tsv file with a header row (or, for JSON, an array of objects).',
    fileToImport: 'File to import',
    needAStartingPoint: 'Need a starting point?',
    downloadTheTemplateCsv: 'Download the template (CSV)',
    excel: 'Excel',
    rowSDetectedMapEachColumn: 'row(s) detected. Map each column to a field.',
    targetForColumnCol: 'Target for column {{col}}',
    ignore: '— Ignore',
    duplicates: 'Duplicates',
    skipDuplicates: 'Skip duplicates',
    updateDuplicates: 'Update duplicates',
    alwaysCreate: 'Always create',
    emailVerification: 'Email verification',
    none: 'None',
    syntax: 'Syntax',
    syntaxMx: 'Syntax + MX',
    addToGroup: 'Add to group',
    none2: 'None',
    defaultStage: 'Default stage',
    duplicatesFile: 'Duplicates (file)',
    duplicatesExisting: 'Duplicates (existing)',
    withoutEmail: 'Without email',
    invalidEmails: 'Invalid emails',
    emailVerificationEmailVerificationLevelP: 'Email verification: {{email_verification_level}}. Preview of the',
    firstRowS: 'first row(s):',
    status: 'Status',
    invalid: '(invalid)',
    importComplete: 'Import complete.',
    createdCreated: 'Created: {{created}}',
    updatedUpdated: 'Updated: {{updated}}',
    duplicatesSkippedSkippedDuplicate: 'Duplicates skipped: {{skipped_duplicate}}',
    invalidEmailsSkippedSkippedInvalidEmail: 'Invalid emails skipped: {{skipped_invalid_email}}',
    withoutEmailSkippedSkippedNoEmail: 'Without email skipped: {{skipped_no_email}}',
    new: 'New',
  },
  journeyPanel: {
    bringForward: 'Bring forward',
    moveBackDowngrade: 'Move back (downgrade)',
    currentStage: 'Current stage',
    downgradeToName: 'Downgrade to “{{name}}”',
    moveToName: 'Move to “{{name}}”',
    cancel: 'Cancel',
    moving: 'Moving…',
    confirm: 'Confirm',
    thisMoveSendsTheLeadBack: 'This move sends the lead back to an earlier stage. It will be logged as a downgrade.',
    reasonOptional: 'Reason (optional)',
    whyThisStageChange: 'Why this stage change?',
    readOnly: 'Read-only',
    downgradeToName2: 'Downgrade to {{name}}',
    moveToName2: 'Move to {{name}}',
    won: 'Won',
    lost: 'Lost',
    couldNotUpdateTheTask: 'Could not update the task.',
    stageChecklist: 'Stage checklist',
    checklistProgress: 'Checklist progress',
    noTaskForThisStage: 'No task for this stage.',
    required: 'required',
    doneV: 'Done {{v}}',
    noStageMoveRecorded: 'No stage move recorded.',
    enteredTheJourneyV: 'Entered the journey · {{v}}',
    downgrade: 'downgrade',
    aiAdviceUnavailableRightNow: 'AI advice unavailable right now.',
    aiAdviceStrategy: 'AI advice / Strategy',
    aiAnalysisOfTheJourneyCharged: 'AI analysis of the journey (charged to your AI credits)',
    analyzing: 'Analyzing…',
    adviseAgain: '↻ Advise again',
    aiAdvice: '✨ AI advice',
    theAiIsBuildingAnEngagement: 'The AI is building an engagement strategy…',
    nextBestActions: 'Next best actions',
    suggestedStage: 'Suggested stage',
    move: 'Move',
    talkingPoints: 'Talking points',
    risks: 'Risks',
    generatedByAiModelChargedTo: 'Generated by AI ({{model}}) · charged to your AI credits',
    journeyUnavailable: 'Journey unavailable.',
    couldNotMove: 'Could not move.',
    loadingTheJourney: 'Loading the journey…',
    currentStageSince: 'Current stage since',
    journeyNotStarted: 'Journey not started',
    moveBack: '← Move back',
    moveForward: 'Move forward →',
    startTheJourney: 'Start the journey →',
    movementHistory: 'Movement history',
    high: 'High',
    low: 'Low',
  },
  leadsKanbanPage: {
    leadScore: 'Lead score',
    checklist: 'Checklist',
    sinceV: 'Since {{v}}',
    leadBoardUnavailable: 'Lead board unavailable.',
    couldNotMoveTheLead: 'Could not move the lead.',
    loadingTheLeadBoard: 'Loading the lead board…',
    leadJourney: 'Lead journey',
    leadSSpreadAcrossEngagementStages: 'lead(s) spread across engagement stages. Drag a card to change its stage.',
    refresh: '↻ Refresh',
    noLeadHasStartedItsJourney: 'No lead has started its journey yet. Open a contact → Journey tab to start.',
  },
  mergeDuplicatePicker: {
    change: 'Change',
    searchForTheDuplicateNameEmail: 'Search for the duplicate (name, email)…',
    searchForTheDuplicateContact: 'Search for the duplicate contact',
    searching: 'Searching…',
    noContactFound: 'No contact found.',
  },
  ownerPicker: {
    admin: 'Admin',
    owner: 'Owner',
    manager: 'Manager',
    member: 'Member',
    viewer: 'Viewer',
    unassigned: 'Unassigned',
    assignTo: 'Assign to…',
    searchForAMember: 'Search for a member…',
    arialabelSearch: '{{ariaLabel}} — search',
    loadingTheDirectory: 'Loading the directory…',
    noMemberFound: 'No member found.',
    me: 'Me',
    outsideTheDirectory: 'Outside the directory',
    userId: 'User ID',
    arialabelIdentifier: '{{ariaLabel}} — identifier',
    directoryUnavailableEnterAnId: 'Directory unavailable — enter an ID…',
    enterAnIdManually: 'Enter an ID manually…',
  },
  pipelinePage: {
    actionFailedTryAgain: 'Action failed. Try again.',
    cancel: 'Cancel',
    deleting: 'Deleting…',
    contact: 'Contact',
    company: 'Company',
    owner: 'Owner',
    expectedCloseDate: 'Expected close date',
    otherCustomFields: 'Other custom fields',
    customFields: 'Custom fields',
    theDealNameIsRequired: 'The deal name is required.',
    theValueMustBeAPositive: 'The value must be a positive number or zero.',
    couldNotCreate: 'Could not create.',
    newDeal: 'New deal',
    creating: 'Creating…',
    create: 'Create',
    name: 'Name',
    value: 'Value',
    currency: 'Currency',
    couldNotSave: 'Could not save.',
    couldNotDelete: 'Could not delete.',
    dealDetails: 'Deal details',
    confirmDeletion: 'Confirm deletion',
    delete: 'Delete',
    saving: 'Saving…',
    save: 'Save',
    deleteTheDealNameThisCannot: 'Delete the deal “{{name}}”? This cannot be undone.',
    stage: 'Stage',
    status: 'Status',
    thePipelineNameIsRequired: 'The pipeline name is required.',
    renameThePipeline: 'Rename the pipeline',
    newPipeline: 'New pipeline',
    youCanAddStagesOnceThe: 'You can add stages once the pipeline is created.',
    theStageNameIsRequired: 'The stage name is required.',
    editTheStage: 'Edit the stage',
    newStage: 'New stage',
    add: 'Add',
    deleteTheStageNameDealsAttached: 'Delete the stage “{{name}}”? Deals attached to this stage will no longer be shown on the board.',
    winProbability: 'Win probability (%)',
    couldNotLoadTheDeals: 'Could not load the deals.',
    couldNotReorderTheStages: 'Could not reorder the stages.',
    couldNotMoveReloading: 'Could not move — reloading.',
    chooseAPipeline: 'Choose a pipeline',
    rename: 'Rename',
    addAStep: '+ Add a step',
    newPipeline2: '+ New pipeline',
    export: 'Export',
    dragAndDropTheCardsBetween: 'Drag and drop the cards between stages.',
    noPipelineCreateOneToOrganize: 'No pipeline. Create one to organize your deals.',
    thisPipelineHasNoStages: 'This pipeline has no stages.',
    win: '% win',
    moveNameToTheLeft: 'Move {{name}} to the left',
    moveNameToTheRight: 'Move {{name}} to the right',
    editTheStageName: 'Edit the stage {{name}}',
    addADealToName: 'Add a deal to {{name}}',
    weightedV: 'Weighted {{v}}',
    dealNameDragToMove: 'Deal {{name}}, drag to move',
    editTheDealName: 'Edit the deal {{name}}',
    winProbabilityV: 'Win probability {{v}}%',
    overdue: 'Overdue',
    close: 'Close',
    noDeals: 'No deals',
    deal: 'Deal',
    deleteThePipeline: 'Delete the pipeline',
    deleteNameAndAllItsStages: 'Delete “{{name}}” and all its stages? This cannot be undone.',
    moveAndDelete: 'Move and delete',
    deleteTheStageName: 'Delete the stage “{{name}}”?',
    stageHasDealsOnlyStage: 'This stage holds {{count}} deal(s) and it is the only stage of the pipeline: add another stage or move these deals before deleting it.',
    moveTheCountDealsTo: 'Move the {{count}} deal(s) to',
    noDealOnThisStage: 'No deal on this stage.',
    pipelineHasDealsNoOtherPipeline: 'This pipeline holds {{count}} deal(s) and no other pipeline can receive them: create another pipeline or delete/move these deals first.',
    noDealInThisPipeline: 'No deal in this pipeline.',
  },
  prospectScoreBadge: {
    gradeGrade: 'Grade {{grade}}',
    leadLeadScoreIcpIcpScore: 'Lead {{lead_score}} · ICP {{icp_score}}/{{icp_max}}',
    icpIcpFitPct: 'ICP {{icp_fit_pct}}%',
    whyTitle: 'Why this score?',
    zeroNotConfigured: 'Score is 0: no ideal customer profile (ICP) is configured.',
    zeroNoData: 'Score is 0: nothing on this record matches your ICP and no interaction is logged.',
    configureIcp: 'Configure the ICP',
    defaultIcpNote: 'Computed with the default ICP profile (editable in settings).',
  },
  prospectingCampaignPage: {
    countResultS: '{{count}} result(s)',
    couldNotLoadTheCampaign: 'Could not load the campaign.',
    theCampaignServiceIsBeingDeployed: 'The campaign service is being deployed.',
    couldNotRetry: 'Could not retry.',
    backToIcps: '← Back to ICPs',
    prospectingCampaignsAreBeingDeployedOn: 'Prospecting campaigns are being deployed on this environment. Step tracking (discovery → scoring → enrichment → grouping → follow-up → enrollment) will be available once the service is live.',
    campaignNotFound: 'Campaign not found.',
    launching: 'Launching…',
    run: 'Run',
    retry: 'Retry',
    refresh: 'Refresh',
    progress: 'Progress',
    campaignProgress: 'Campaign progress',
    stepChain: 'Step chain',
    results: 'Results',
    discovered: 'Discovered',
    scored: 'Scored',
    enriched: 'Enriched',
    grouped: 'Grouped',
    followed: 'Followed',
    enrolled: 'Enrolled',
    skippedCasl: 'Skipped (CASL)',
    prospect: 'prospect',
    notEnrolled: 'not enrolled',
    caslLaw25ConsentNotVerified: ': CASL/Law 25 consent not verified — sending is blocked for safety (fail-closed).',
  },
  prospectingGoalsPage: {
    icpProspectingIsBeingDeployedOn: 'ICP prospecting is being deployed on this environment. You can compose and preview your ICP below; saving and launching campaigns will be available once the service is live.',
    none: '— None —',
    removeV: 'Remove {{v}}',
    min: 'min',
    minimum: 'Minimum',
    max: 'max',
    maximum: 'Maximum',
    filled: 'Filled',
    empty: 'Empty',
    toComplete: 'To complete',
    icpCriteria: 'ICP criteria',
    couldNotSaveTryAgain: 'Could not save. Try again.',
    editTheIcp: 'Edit the ICP',
    newIcp: 'New ICP',
    icpName: 'ICP name',
    shopifyDtcFounders: 'Shopify DTC founders',
    goalFreeText: 'Goal (free text)',
    describeYourTargetAndTheBusiness: 'Describe your target and the business intent (10 characters min.).',
    find50FoundersOfShopifyDtc: 'Find 50 founders of Shopify DTC brands in France ready to delegate their social content.',
    firmographicCriteria: 'Firmographic criteria',
    industry: 'Industry',
    saasECommerce: 'SaaS, e-commerce…',
    jobTitle: 'Job title',
    cmoHeadOfGrowth: 'CMO, Head of Growth…',
    seniority: 'Seniority',
    cLevelVpDirector: 'C-level, VP, Director…',
    localization: 'Localization',
    montrealFrance: 'Montreal, France…',
    technologies: 'Technologies',
    headcount: 'Headcount',
    numberOfEmployeesOfTheCompany: 'Number of employees of the company.',
    revenueK: 'Revenue (k$)',
    socialDiscovery: 'Social discovery',
    discoveryByKeywordSubscribersYoutubeOr: 'Discovery by keyword / subscribers (YouTube) or by handle (Instagram, Facebook) — lawful sources only, never “all followers”.',
    platform: 'Platform',
    themeNiche: 'Theme / niche',
    b2bMarketingFitness: 'B2B marketing, fitness…',
    accountHandlesInstagramFacebook: 'Account handles (Instagram / Facebook)',
    brandCreatorEnterToAdd: '@brand, @creator — Enter to add',
    minimumFollowers: 'Minimum followers',
    verifiableCriteria: 'Verifiable criteria',
    readyToConfirm: 'Ready to confirm',
    criterion: 'criterion',
    toComplete2: 'to complete',
    addAtLeastOneCriterion: 'Add at least one criterion',
    saving: 'Saving…',
    save: 'Save',
    createTheIcp: 'Create the ICP',
    cancel: 'Cancel',
    draft: 'Draft',
    confirmed: 'Confirmed',
    inCampaign: 'In campaign',
    archived: 'Archived',
    filled2: 'filled',
    needinputToComplete: '· {{needInput}} to complete',
    lastCampaignLaunchedOn: 'Last campaign launched on',
    edit: 'Edit',
    view: 'View',
    confirmTheIcp: 'Confirm the ICP',
    completeTheToCompleteCriteriaBefore: 'Complete the “to complete” criteria before confirming.',
    confirm: 'Confirm',
    launchACampaign: 'Launch a campaign',
    delete: 'Delete',
    couldNotLoadTheProspectingIcps: 'Could not load the prospecting ICPs.',
    couldNotConfirmIncompleteCriteria: 'Could not confirm (incomplete criteria?).',
    couldNotLaunchTheCampaign: 'Could not launch the campaign.',
    goalsIcp: 'Goals & ICP',
    defineYourIdealCustomerCheckThe: 'Define your ideal customer, check the criteria, then launch a multi-step prospecting campaign.',
    composeAnIcp: 'Compose an ICP',
    recentCampaigns: 'Recent campaigns',
    noIcpYetCreateYourFirst: 'No ICP yet. Create your first ideal customer profile.',
    deleteThisIcp: 'Delete this ICP?',
    nameAndItsCriteriaWillBe: '“{{name}}” and its criteria will be deleted. Campaigns already launched are kept.',
  },
  pushToSequenceButton: {
    couldNotLoadTheSequences: 'Could not load the sequences.',
    enrollmentFailed: 'Enrollment failed.',
    addToASequence: 'Add to a sequence',
    loading: 'Loading…',
    noActiveSequence: 'No active sequence.',
    enrolling: 'Enrolling…',
    enrollLength: 'Enroll {{length}}',
    enrolledEnrolledRequestedCaslSkippedCasl: 'Enrolled {{enrolled}}/{{requested}} · CASL {{skipped_casl}} · no email',
    alreadySuppressedSkippedDownstream: ' · already/suppressed {{skipped_downstream}}',
  },
  quizBuilder: {
    nextDefault: 'Next (default)',
    goToStepV: 'Go to step {{v}}',
    resultVV2: 'Result {{v}} — {{v2}}',
    finishResultBasedOnTheScore: 'Finish (result based on the score)',
    option: 'Option',
    answerLabel: 'Answer label',
    optionLabel: 'Option label',
    score: 'Score',
    crmTag: 'CRM tag',
    eGHot: 'e.g. hot',
    optionTag: 'Option tag',
    branching: 'Branching',
    removeTheOption: 'Remove the option',
    remove: 'Remove',
    questionV: 'Question {{v}}',
    questionText: 'Question text',
    type: 'Type',
    questionType: 'Question type',
    required: 'Required',
    deleteTheQuestion: 'Delete the question',
    freeAnswerSavedInTheContact: 'Free answer: saved in the contact\'s history, without a score.',
    addAnOption: '+ Add an option',
    resultTitleV: 'Result title {{v}}',
    eGHotProspect: 'e.g. Hot prospect',
    resultTitle: 'Result title',
    resultCrmTag: 'Result CRM tag',
    eGQuizHot: 'e.g. quiz-hot',
    description: 'Description',
    minimumScore: 'Minimum score',
    maximumScoreOptional: 'Maximum score (optional)',
    buttonLabelCta: 'Button label (CTA)',
    buttonUrl: 'Button URL',
    https: 'https://…',
    defaultResultIfNoThresholdMatches: 'Default result (if no threshold matches)',
    deleteThisResult: 'Delete this result',
    completeTheQuizToTestIt: 'Complete the quiz to test it:',
    noResults: 'No results.',
    answerAsAVisitorTheScore: 'Answer as a visitor: the score, tags and result page show at the end.',
    actuallySubmitCreatesUpdatesATest: 'Actually submit (creates/updates a test contact)',
    saveTheFormToEnableReal: '(save the form to enable real submission)',
    stepV: 'Step {{v}}',
    stepsLength: 'Steps ({{length}})',
    resultsLength: 'Results ({{length}})',
    test: 'Test',
    quizBuilder: 'Quiz builder',
    point: 'point',
    toFix: 'to fix',
    stepTitle: 'Step title',
    afterThisStep: 'After this step',
    moveStepUp: 'Move step up',
    moveStepDown: 'Move step down',
    delete: 'Delete',
    addAQuestion: '+ Add a question',
    addAStep: '+ Add a step',
    anOptionSBranchingWinsOver: 'An option\'s branching wins over the step\'s, which wins over “next step”. Scores add up along the path actually taken.',
    addAResult: '+ Add a result',
    applyTheQuizScoreToThe: 'Apply the quiz score to the contact\'s lead score',
    theFirstResultWhoseScoreRange: 'The first result whose score range matches is shown; otherwise the “default” result.',
  },
  remindersPage: {
    finish: 'Finish',
    done: 'Done',
    couldNotLoadTheReminders: 'Could not load the reminders.',
    couldNotCompleteTheReminder: 'Could not complete the reminder.',
    dueOverdue: 'Due / overdue',
    noRemindersDueEverythingIsUp: 'No reminders due. Everything is up to date.',
    upcoming: 'Upcoming',
    noRemindersPlannedInTheNext: 'No reminders planned in the next {{days}} days.',
  },
  savedSearchesPage: {
    searchV: 'search “{{v}}”',
    ownerV: 'owner: {{v}}',
    noCriteria: 'no criteria',
    couldNotLoadTheDynamicLists: 'Could not load the dynamic lists.',
    dynamicListCreated: 'Dynamic list created.',
    theCreationFailed: 'The creation failed.',
    couldNotEvaluateTheList: 'Could not evaluate the list.',
    listRefreshedCountContactS: 'List refreshed: {{count}} contact(s).',
    theRefreshFailed: 'The refresh failed.',
    dynamicLists: 'Dynamic lists',
    savedCriteriaDrivenSearchesEvaluateThem: 'Saved, criteria-driven searches: evaluate them live or materialize them into a group.',
    cancel: 'Cancel',
    newList: '+ New list',
    name: 'Name',
    eGHotLeadsQuebec: 'E.g. Hot leads Quebec',
    textSearch: 'Text search',
    nameEmailCompany: 'name, email, company…',
    lifecycleStage: 'Lifecycle stage',
    all: 'All',
    ownerId: 'Owner (ID)',
    userId: 'user id',
    tags: 'Tags',
    commaSeparated: 'Comma-separated.',
    vipNewsletter: 'vip, newsletter',
    createTheList: 'Create the list',
    noDynamicListsCreateOneTo: 'No dynamic lists. Create one to segment your contacts.',
    dynamic: 'dynamic',
    contactS: 'contact(s)',
    refreshedOnV: ' · refreshed on {{v}}',
    results: 'Results',
    refresh: 'Refresh',
    delete: 'Delete',
    evaluating: 'Evaluating…',
    totalMatchingContactS: '{{total}} matching contact(s)',
    noContactCurrentlyMatches: 'No contact currently matches.',
    deleteTheList: 'Delete the list',
    nameWillBeDeletedTheContacts: '“{{name}}” will be deleted. The contacts are not affected.',
    listDeleted: 'List deleted.',
    automaticAssignmentSaved: 'Automatic assignment saved.',
    savingFailed: 'Saving failed.',
    defaultAutomaticAssignment: 'Default automatic assignment',
    theFallbackAppliedToEveryCaptured: 'The fallback applied to every captured lead when no assignment rule matches.',
    enableDefaultAutomaticAssignment: 'Enable default automatic assignment',
    strategy: 'Strategy',
    roundRobin: 'Round-robin',
    loadBalancing: 'Load balancing',
    territory: 'Territory',
    membersPool: 'Members (pool)',
    userIdsCommaSeparated: 'User IDs, comma-separated.',
    user1User2: 'user1, user2',
    saving: 'Saving…',
    save: 'Save',
  },
  savedViewsBar: {
    couldNotSaveTheView: 'Could not save the view.',
    views: 'Views:',
    deleteTheViewName: 'Delete the view {{name}}',
    saveTheView: '+ Save the view',
    saveTheView2: 'Save the view',
    viewName: 'View name',
    eGHotProspects: 'E.g. Hot prospects',
    deleteTheView: 'Delete the view',
    deleteTheViewName2: 'Delete the view “{{name}}”?',
  },
  schedulerPage: {
    appointmentScheduling: 'Appointment scheduling',
    noAvailability: 'No availability',
    none: 'None',
    inPerson: 'In person',
    customLink: 'Custom link',
    googleMeet: 'Google Meet',
    address: 'Address',
    n123MainStreetMontreal: '123 Main Street, Montreal',
    phoneNumber: 'Phone number',
    meetingLink: 'Meeting link',
    shortText: 'Short text',
    longText: 'Long text',
    phone: 'Phone',
    dropdownList: 'Dropdown list',
    confirmed: 'Confirmed',
    cancelled: 'Cancelled',
    declined: 'Declined',
    rescheduled: 'Rescheduled',
    done: 'Done',
    noShow: 'No-show',
    pending: 'Pending',
    theTitleIsRequired: 'The title is required.',
    thePublicLinkSlugIsRequired: 'The public link (slug) is required.',
    enterTheLocationOrTheConference: 'Enter the location or the conference link.',
    theOwnerSEmailAddressIs: 'The owner\'s email address is invalid.',
    theMaximumBookingsPerDayMust: 'The maximum bookings per day must be at least 1.',
    theBookingWindowMustBeBetween: 'The booking window must be between 1 and 730 days.',
    theCapacitySeatsPerSlotMust: 'The capacity (seats per slot) must be at least 1.',
    eachCustomQuestionMustHaveA: 'Each custom question must have a label.',
    theQuestionLabelDropdownMustOffer: 'The question “{{label}}” (dropdown) must offer at least one option.',
    couldNotSave: 'Could not save.',
    editTheBookingPage: 'Edit the booking page',
    newBookingPage: 'New booking page',
    cancel: 'Cancel',
    saving: 'Saving…',
    save: 'Save',
    title: 'Title',
    productDemo30Min: 'Product demo (30 min)',
    publicLink: 'Public link',
    productDemo: 'product-demo',
    description: 'Description',
    whatTheGuestWillSeeBefore: 'What the guest will see before booking.',
    durationMin: 'Duration (min)',
    bufferMin: 'Buffer (min)',
    timeZone: 'Time zone',
    conferenceLocation: 'Conference & location',
    howTheGuestJoinsYouSets: 'How the guest joins you — sets each booking\'s meeting link',
    conferenceType: 'Conference type',
    locationFreeTextOptional: 'Location (free text, optional)',
    extraDetailShownToTheGuest: 'Extra detail shown to the guest (e.g. “2nd floor”)',
    availability: 'Availability',
    weeklyRulesAndDateExceptions: 'Weekly rules and date exceptions',
    weeklyAvailability: 'Weekly availability',
    addASlot: '+ Add a slot',
    noAvailabilityAddAtLeastOne: 'No availability — add at least one slot.',
    day: 'Day',
    start: 'Start',
    end: 'End',
    remove: 'Remove',
    dateExceptions: 'Date exceptions',
    addADate: '+ Add a date',
    noExceptionsAddADateTo: 'No exceptions. Add a date to block it or give it specific hours.',
    date: 'Date',
    blockedDay: 'Blocked day',
    removeTheDate: 'Remove the date',
    addARange: '+ Add a range',
    limitsNotice: 'Limits & notice',
    minimumNoticeDailyCapReschedulesAnd: 'Minimum notice, daily cap, reschedules and notifications',
    minimumNotice: 'Minimum notice',
    minimumDelayBeforeASlotCan: 'Minimum delay before a slot can be booked.',
    noticeUnit: 'Notice unit',
    maxBookingsDay: 'Max. bookings / day',
    leaveEmptyForUnlimited: 'Leave empty for unlimited.',
    unlimited: 'Unlimited',
    reschedulesAllowed: 'Reschedules allowed',
    numberOfTimesAGuestCan: 'Number of times a guest can move their appointment.',
    ownerEmail: 'Owner email',
    whereToReceiveBookingNotifications: 'Where to receive booking notifications.',
    youExampleCom: 'you@example.com',
    visibilityConfirmationConsent: 'Visibility, confirmation & consent',
    publicOrPrivateChannelAutomaticOr: 'Public or private channel, automatic or manual confirmation, consent notice (Law 25)',
    visibility: 'Visibility',
    aPrivateChannelIsOnlyReachable: 'A private channel is only reachable through its secret link.',
    public: 'Public',
    privateSecretLink: 'Private (secret link)',
    confirmation: 'Confirmation',
    manualHoldsEachRequestUntilYou: '“Manual” holds each request until you approve it.',
    automatic: 'Automatic',
    manualToApprove: 'Manual (to approve)',
    privateLink: 'Private link',
    copyThePrivateLink: 'Copy the private link',
    thisTokenIsRevocableSavingThe: 'This token is revocable: saving the page again may renew it.',
    aPrivateLinkSecretTokenWill: 'A private link (secret token) will be generated on save.',
    consentNoticeLaw25: 'Consent notice (Law 25)',
    shownOnThePublicFormIf: 'Shown on the public form; if filled in, the guest must consent to book.',
    iAgreeThatMyContactDetails: 'I agree that my contact details may be used to arrange this appointment.',
    windowBuffersCapacity: 'Window, buffers & capacity',
    bookingHorizonBeforeAfterBuffersAnd: 'Booking horizon, before/after buffers and group appointments',
    bookingWindowDays: 'Booking window (days)',
    howManyDaysInAdvanceThe: 'How many days in advance the guest can book.',
    seatsPerSlot: 'Seats per slot',
    groupAppointmentLeaveEmptyForA: 'Group appointment. Leave empty for a one-on-one (1:1) appointment.',
    bufferBeforeMin: 'Buffer before (min)',
    timeReservedBeforeEachAppointment: 'Time reserved before each appointment.',
    bufferAfterMin: 'Buffer after (min)',
    timeReservedAfterEachAppointment: 'Time reserved after each appointment.',
    theSpacingActuallyAppliedBetweenTwo: 'The spacing actually applied between two appointments is the largest of the buffers (symmetric buffer, before, after).',
    teamHostAssignment: 'Team & host assignment',
    distributeBookingsAmongSeveralHostsRound: 'Distribute bookings among several hosts (round-robin or least busy)',
    leaveTheListEmptyForA: 'Leave the list empty for a single host (the page owner). Add teammates to automatically distribute each new booking among them.',
    assignmentStrategy: 'Assignment strategy',
    roundRobinAlternatesHostsLeastBusy: '“Round-robin” alternates hosts; “Least busy” picks the one with the fewest upcoming appointments.',
    hostsInTheRotationLength: 'Hosts in the rotation ({{length}})',
    addAHost: 'Add a host',
    noAdditionalHostBookingsGoTo: 'No additional host — bookings go to the page owner.',
    cancellationAndReschedulePolicies: 'Cancellation and reschedule policies',
    minimumNoticeForAGuestTo: 'Minimum notice for a guest to cancel or move, and required reason',
    cancellationNotice: 'Cancellation notice',
    minimumDelayBeforeTheAppointmentTo: 'Minimum delay before the appointment to cancel.',
    cancellationNoticeUnit: 'Cancellation notice unit',
    rescheduleNotice: 'Reschedule notice',
    minimumDelayBeforeTheAppointmentTo2: 'Minimum delay before the appointment to move it.',
    rescheduleNoticeUnit: 'Reschedule notice unit',
    requireACancellationReason: 'Require a cancellation reason',
    emailReminders: 'Email reminders',
    whenToSendAReminderTo: 'When to send a reminder to the guest before the appointment',
    reminderValue: 'Reminder value',
    reminderUnit: 'Reminder unit',
    add: 'Add',
    removeReminderV: 'Remove reminder {{v}}',
    customQuestions: 'Custom questions',
    extraFieldsAskedOfTheGuest: 'Extra fields asked of the guest when booking',
    noQuestionsTheFormAlreadyAsks: 'No questions. The form already asks for name, email, phone and notes.',
    label: 'Label',
    orderNumber: 'Order number',
    type: 'Type',
    optionsOnePerLine: 'Options (one per line)',
    optionAOptionB: 'Option A\nOption B',
    mandatory: 'Mandatory',
    removeTheQuestion: 'Remove the question',
    addAQuestion: '+ Add a question',
    confirmationRedirect: 'Confirmation & redirect',
    messageShownAfterBookingAndOptional: 'Message shown after booking and optional redirect',
    confirmationMessage: 'Confirmation message',
    thankYouYouWillReceiveA: 'Thank you! You will receive a confirmation email.',
    redirectUrlOptional: 'Redirect URL (optional)',
    theGuestIsRedirectedThereAfter: 'The guest is redirected there after booking.',
    httpsExampleComThanks: 'https://example.com/thanks',
    pageActiveAcceptsBookings: 'Page active (accepts bookings)',
    confirmTheAppointment: 'Confirm the appointment',
    theGuestWillReceiveAConfirmation: 'The guest will receive a confirmation email and the appointment moves to “Confirmed”.',
    declineTheRequest: 'Decline the request',
    theGuestWillBeToldAbout: 'The guest will be told about the refusal and the slot is freed.',
    markAsNoShow: 'Mark as no-show',
    theAppointmentWillBeMarkedNo: 'The appointment will be marked “No-show”.',
    markNoShow: 'Mark no-show',
    cancelTheAppointment: 'Cancel the appointment',
    theGuestWillBeInformedAnd: 'The guest will be informed and the slot is freed.',
    privateNote: 'Private note',
    visibleOnlyToYourTeamNever: 'Visible only to your team, never to the guest.',
    saveTheNote: 'Save the note',
    aReasonIsRequiredToCancel: 'A reason is required to cancel this appointment.',
    theNoteCannotBeEmpty: 'The note cannot be empty.',
    actionNotPossible: 'Action not possible.',
    close: 'Close',
    oneMoment: 'One moment…',
    guest: 'Guest',
    note: 'Note',
    reason: 'Reason',
    reasonOptional: 'Reason (optional)',
    internalContext: 'Internal context…',
    reasonSharedWithTheGuest: 'Reason shared with the guest…',
    couldNotLoadTheSlots: 'Could not load the slots.',
    couldNotReschedule: 'Could not reschedule.',
    rescheduleTheAppointment: 'Reschedule the appointment',
    rescheduling: 'Rescheduling…',
    reschedule: 'Reschedule',
    currentlyVChooseANewSlot: 'Currently: {{v}}. Choose a new slot.',
    loadingSlots: 'Loading slots…',
    noSlotAvailableWithinTheBooking: 'No slot available within the booking window.',
    googleCalendar: 'Google Calendar',
    caldav: 'CalDAV',
    icsFeed: 'ICS feed',
    appleIcloud: 'Apple (iCloud)',
    checkForConflicts: 'Check for conflicts',
    write: 'Write',
    couldNotLoadTheCalendars: 'Could not load the calendars.',
    theCalendar: 'the calendar',
    labelWasConnectedSuccessfully: '{{label}} was connected successfully.',
    connectingLabelFailedVTryAgain: 'Connecting {{label}} failed{{v}}. Try again.',
    setupRequiredGoogleMicrosoftOauthCredent: 'Setup required (Google/Microsoft OAuth credentials).',
    couldNotStartTheOauthConnection: 'Could not start the OAuth connection.',
    pasteTheIcsFeedAddress: 'Paste the ICS feed address.',
    theFeedAddressMustStartWith: 'The feed address must start with http:// or https://.',
    couldNotConnect: 'Could not connect.',
    couldNotDelete: 'Could not delete.',
    connectedCalendars: 'Connected calendars',
    aConnectedIcsFeedAutomaticallyRemoves: 'A connected ICS feed automatically removes your busy times from your availability.',
    connectACalendar: 'Connect a calendar',
    noConnectedCalendar: 'No connected calendar.',
    inactive: 'Inactive',
    removing: 'Removing…',
    calendarType: 'Calendar type',
    icsFeed2: ' (ICS feed)',
    authorizeCalendarProviderLabelsForTwo: 'Authorize {{CALENDAR_PROVIDER_LABELS}} for two-way sync: your busy times are removed from your availability and each confirmed appointment is added to your calendar.',
    redirecting: 'Redirecting…',
    connectWithV: 'Connect with {{v}}',
    setupRequiredGoogleMicrosoftOauthCredent2: 'Setup required (Google/Microsoft OAuth credentials). The connection to',
    isNotEnabledOnThisAccount: 'is not enabled on this account yet. Meanwhile, export a private ICS feed from {{CALENDAR_PROVIDER_LABELS}} and connect it via “ICS feed”.',
    icsFeedAddress: 'ICS feed address',
    icalIcsLinkExportedByYour: '“iCal” / “ICS” link exported by your calendar. It is only used to read your busy times.',
    httpsCalendarExampleComFeedIcs: 'https://calendar.example.com/feed.ics',
    connecting: 'Connecting…',
    connect: 'Connect',
    couldNotLoadTheCalendlyStatus: 'Could not load the Calendly status.',
    calendlyWasConnectedSuccessfully: 'Calendly was connected successfully.',
    connectingCalendlyFailedVTryAgain: 'Connecting Calendly failed{{v}}. Try again.',
    setupRequiredCalendlyOauthCredentialsOn: 'Setup required (Calendly OAuth credentials on the server).',
    couldNotStartTheCalendlyConnection: 'Could not start the Calendly connection.',
    eventTypesEventTypeS: '{{event_types}} event type(s)',
    bookingsImportedBookingS: '{{bookings_imported}} booking(s)',
    importedV: 'Imported: {{v}}.',
    syncComplete: 'Sync complete.',
    couldNotSyncCalendly: 'Could not sync Calendly.',
    calendlyWebhookEnabledNewBookingsWill: 'Calendly webhook enabled — new bookings will arrive automatically.',
    calendlyWebhookDisabled: 'Calendly webhook disabled.',
    couldNotUpdateTheCalendlyWebhook: 'Could not update the Calendly webhook.',
    importYourCalendlyEventTypesAnd: 'Import your Calendly event types and bookings, and receive new bookings automatically.',
    account: 'Account:',
    email: 'Email:',
    organization: 'Organization:',
    eventTypes: 'Event types:',
    lastSync: 'Last sync:',
    openMyCalendlyPage: 'Open my Calendly page',
    webhookActive: 'Webhook active',
    webhookInactive: 'Webhook inactive',
    syncing: 'Syncing…',
    syncNow: 'Sync now',
    disableTheWebhook: 'Disable the webhook',
    enableTheWebhook: 'Enable the webhook',
    setupRequiredTheCalendlyAppOauth: 'Setup required: the Calendly app OAuth credentials are not configured on this account yet. Once the administrator configures them, the “Connect Calendly” button will start the authorization.',
    connectYourCalendlyAccountToImport: 'Connect your Calendly account to import your availability and centralize your bookings in the CRM.',
    connectCalendly: 'Connect Calendly',
    couldNotLoad: 'Could not load.',
    bookingsTitle: 'Bookings — {{title}}',
    copyThePublicLink: 'Copy the public link',
    noBookingsYet: 'No bookings yet.',
    sourceSource: 'Source: {{source}}',
    hostV: 'Host: {{v}}',
    reasonCancelReason: 'Reason: {{cancel_reason}}',
    consentCollectedLaw25: 'Consent collected (Law 25)',
    rescheduledRescheduleCount: 'Rescheduled {{reschedule_count}}×',
    guestLinkManagement: 'Guest link (management)',
    downloadIcs: 'Download .ics',
    confirm: 'Confirm',
    deny: 'Deny',
    markNoShow2: 'Mark no-show',
    editTheNote: 'Edit the note',
    addANote: 'Add a note',
    couldNotLoadTheBookingPages: 'Could not load the booking pages.',
    publicBookingPagesCalendlyStyleLinked: 'Public booking pages (Calendly-style), linked to your contacts and deals.',
    newPage: 'New page',
    noBookingPagesCreateOneTo: 'No booking pages. Create one to share your availability.',
    createAPage: 'Create a page',
    noticeV: 'notice {{v}}',
    private: 'private',
    manualConfirmation: 'manual confirmation',
    lengthHostsHostAssignmentStrategyLabels: '{{length}} hosts · {{HOST_ASSIGNMENT_STRATEGY_LABELS}}',
    min: 'min',
    active: 'Active',
    inactive2: 'Inactive',
    copyLink: 'Copy link',
    bookings: 'Bookings',
    edit: 'Edit',
    delete: 'Delete',
    deleteThePage: 'Delete the page?',
    titleAndItsPublicLinkWill: '“{{title}}” and its public link will be deleted.',
    hours: 'hours',
    days: 'days',
    email2: 'Email',
  },
  shares: {
    sharedWithAtLeastOneTeam: 'Shared with at least one team member',
    shared: 'Shared',
    okSharesCreated: '{{ok}} shares created.',
    shareCreated: 'Share created.',
    okShareSCreatedFailedFailure: '{{ok}} share(s) created, {{failed}} failure(s).',
    sharingFailedCheckYourPermissionsAnd: 'Sharing failed. Check your permissions and try again.',
    shareRevoked: 'Share revoked.',
    couldNotRevokeThisShare: 'Could not revoke this share.',
    notificationResent: 'Notification resent.',
    couldNotResendTheNotification: 'Could not resend the notification.',
    shareWith: 'Share with…',
    close: 'Close',
    sharing: 'Sharing…',
    shareV: 'Share{{v}}',
    onlyOwnersAndAdminsCanShare: 'Only owners and admins can share data.',
    tooManySharesAtOnceGrantcount: 'Too many shares at once ({{grantCount}}). Reduce the selection (max {{MAX_GRANTS_PER_SUBMIT}}).',
    share: 'Share',
    lengthItems: '{{length}} items',
    with: 'with:',
    members: 'Members',
    loadingTheDirectory: 'Loading the directory…',
    memberDirectoryUnavailable: 'Member directory unavailable.',
    noTeammatesYetInviteYourTeam: 'No teammates yet. Invite your team from the Team tab.',
    membersToShareWith: 'Members to share with',
    awaitingInvitationAcceptance: 'Awaiting invitation acceptance',
    pending: 'pending',
    permission: 'Permission',
    existingShares: 'Existing shares',
    loading: 'Loading…',
    noActiveShareYet: 'No active share yet.',
    allContacts: ' · all contacts',
    resendTheShareNotificationInApp: 'Resend the share notification (in-app + email)',
    sending: 'Sending…',
    resend: 'Resend',
    revoking: 'Revoking…',
    revoke: 'Revoke',
  },
  sharesPage: {
    allData: 'All data',
    group: 'Group',
    contact: 'Contact',
    couldNotLoadTheShares: 'Could not load the shares.',
    groupV: 'Group “{{v}}”',
    contactV: 'Contact “{{v}}”',
    shareRevoked: 'Share revoked.',
    couldNotRevokeThisShare: 'Could not revoke this share.',
    notificationResentToTheRecipient: 'Notification resent to the recipient.',
    couldNotResendTheNotification: 'Could not resend the notification.',
    reservedForOrganizationOwnersAdmins: 'Reserved for organization owners / admins.',
    shares: 'Shares',
    everythingYourOrganizationHasSharedGroup: 'Everything your organization has shared (groups, contacts, or all data), with whom, and at what access level. Revoke or resend the notification in one click.',
    refresh: 'Refresh',
    loading: 'Loading…',
    noActiveShareShareAGroup: 'No active share. Share a group or contacts from Contacts or Groups (“Share” button).',
    recipient: 'Recipient',
    resource: 'Resource',
    access: 'Access',
    sharedBy: 'Shared by',
    actions: 'Actions',
    you: 'You',
    resendTheNotificationInAppEmail: 'Resend the notification (in-app + email)',
    resend: 'Resend',
    revoke: 'Revoke',
    couldNotLoadTheAssignments: 'Could not load the assignments.',
    chooseAnArtifactAndAMember: 'Choose an artifact and a member.',
    artifactAssignedToTheMember: 'Artifact assigned to the member.',
    couldNotAssignAlreadyAssignedOr: 'Could not assign (already assigned, or insufficient rights).',
    assignmentRemoved: 'Assignment removed.',
    couldNotRemoveThisAssignment: 'Could not remove this assignment.',
    artifactAssignments: 'Artifact assignments',
    giveResponsibilityForASavedSearch: 'Give responsibility for a saved search, a prospect list or an account file to a team member to share the work.',
    artifactType: 'Artifact type',
    artifact: 'Artifact',
    noVAvailable: 'No {{v}} available',
    choose: '— Choose —',
    member: 'Member',
    noActiveMember: 'No active member',
    assigning: 'Assigning…',
    assign: 'Assign',
    noAssignmentsAssignAnArtifactTo: 'No assignments. Assign an artifact to a member above.',
    type: 'Type',
    assignedTo: 'Assigned to',
    assignedBy: 'Assigned by',
    remove: 'Remove',
  },
  signalsModel: {
    websiteVisit: 'Website visit',
    pricingPageView: 'Pricing page view',
    demoRequest: 'Demo request',
    contentDownload: 'Content download',
    webinarAttended: 'Webinar attended',
    competitorResearch: 'Competitor research',
    g2CategoryView: 'G2 category view',
    repeatVisit: 'Repeat visit',
    highIntentKeyword: 'High-intent keyword',
    veryHot: 'Very hot',
    hot: 'Hot',
    warm: 'Warm',
    neutral: 'Neutral',
  },
  signalsSignalsPage: {
    autoPlaceholder: 'auto',
    sessionExpiredSignInAgain: 'Session expired — sign in again.',
    actionReservedForAnEditorAdministrator: 'Action reserved for an editor/administrator role — your role is read-only.',
    signalType: 'Signal type',
    contactId: 'Contact (ID)',
    contactEmail: 'Contact email',
    companyId: 'Company (ID)',
    source: 'Source',
    noteOptional: 'Note (optional)',
    couldNotLoadTheRules: 'Could not load the rules.',
    couldNotLoadTheSignalFeed: 'Could not load the signal feed.',
    chooseASignalType: 'Choose a signal type.',
    signalSaved: 'Signal saved.',
    couldNotSaveTheSignal: 'Could not save the signal.',
    intentSignals: 'Intent signals',
    buyingSignalsFeedTheLeadScore: 'Buying signals feed the lead score: each signal adds its weight to the linked contact\'s score and leaves a trace in their timeline.',
    signals: 'Signals',
    cumulativeScore: 'Cumulative score',
    contactsReached: 'Contacts reached',
    dominantType: 'Dominant type',
    countSignalS: '{{count}} signal(s)',
    recordASignal: 'Record a signal',
    idOfTheLinkedContactOr: 'ID of the linked contact — or enter the email below.',
    contact: 'contact_…',
    resolvedToAnExistingContactIf: 'Resolved to an existing contact if the ID is missing.',
    prospectExampleCom: 'prospect@example.com',
    company: 'company_…',
    webTracking: 'web-tracking',
    weightOverride: 'Weight (override)',
    emptyTheRuleSDefaultWeight: 'Empty = the rule\'s default weight.',
    signalContext: 'Signal context…',
    saving: 'Saving…',
    recordTheSignal: 'Record the signal',
    yourRoleIsReadOnlyRecording: 'Your role is read-only: recording signals is reserved for editor or administrator roles. The feed and the rules remain viewable.',
    signalFeed: 'Signal feed',
    filterByType: 'Filter by type',
    allTypes: 'All types',
    contactId2: 'Contact ID',
    filterByContact: 'Filter by contact',
    companyId2: 'Company ID',
    filterByCompany: 'Filter by company',
    reset: 'Reset',
    loadingTheFeed: 'Loading the feed…',
    noSignalForThisFilterSignals: 'No signal for this filter. Signals appear here as soon as a contact shows intent (pricing visit, demo request, high-intent keyword…).',
    contact2: 'contact',
    unresolvedContact: 'unresolved contact',
    company2: 'company',
    sourceSource: 'source {{source}}',
    triggeringContacts: 'Triggering contacts',
    noContactResolvedOnTheseSignals: 'No contact resolved on these signals yet.',
    signalS: 'signal(s) ·',
    triggeringCompanies: 'Triggering companies',
    noCompanyLinkedToTheseSignals: 'No company linked to these signals.',
    scoreRules: 'Score rules',
    defaultWeightAddedToTheLead: 'Default weight added to the lead score for each signal type. These weights are built into the engine; when recording, the “Weight” field lets you override the value of a specific signal.',
    loadingRules: 'Loading rules…',
    noScoreRuleDefinedOnThis: 'No score rule defined on this backend.',
  },
  webFormsPage: {
    fieldCountOne: '{{count}} field',
    fieldCountMany: '{{count}} fields',
    email: 'Email',
    firstName: 'First name',
    theNameIsRequired: 'The name is required.',
    addAtLeastOneField: 'Add at least one field.',
    incompleteQuizQerrors: 'Incomplete quiz: {{qErrors}}',
    aQuizNeedsAFieldMapped: 'A quiz needs a field mapped to “Email” to send the result.',
    couldNotSave: 'Could not save.',
    editTheForm: 'Edit the form',
    newForm: 'New form',
    cancel: 'Cancel',
    saving: 'Saving…',
    save: 'Save',
    formName: 'Form name',
    demoRequest: 'Demo request',
    description: 'Description',
    targetGroup: 'Target group',
    createdContactsAreAddedToIt: 'Created contacts are added to it.',
    none: 'None',
    redirectAfterSubmit: 'Redirect after submit',
    optionalUrlThankYouBackTo: 'Optional URL (thank-you / back to site).',
    https: 'https://…',
    formFields: 'Form fields',
    addAField: '+ Add a field',
    fieldLabel: 'Field label',
    labelEGEmail: 'Label (e.g. Email)',
    fieldType: 'Field type',
    mapTo: 'Map to',
    contactFieldFilledByThisField: 'Contact field filled by this field',
    required: 'Required',
    remove: 'Remove',
    formActiveAcceptsSubmissions: 'Form active (accepts submissions)',
    requireExplicitConsentCheckboxCaslLaw: 'Require explicit consent (checkbox — CASL / Law 25)',
    consentText: 'Consent text',
    iAgreeToReceiveCommunications: 'I agree to receive communications…',
    turnIntoAQuizMultiStep: 'Turn into a quiz / multi-step funnel (scoring, tags, branching, results)',
    couldNotLoadTheForms: 'Could not load the forms.',
    webCaptureFormsEachSubmissionAutomatical: 'Web capture forms — each submission automatically creates a contact.',
    noFormsCreateOneToCapture: 'No forms. Create one to capture leads from your website.',
    createAForm: 'Create a form',
    lengthStepSLength2ResultS: '{{length}} step(s) · {{length2}} result(s)',
    quiz: 'Quiz',
    active: 'Active',
    inactive: 'Inactive',
    submission: 'submission',
    copyTheEmbed: 'Copy the embed',
    edit: 'Edit',
    delete: 'Delete',
    deleteTheForm: 'Delete the form?',
    nameAndItsEmbedLinkWill: '“{{name}}” and its embed link will be deleted.',
    webForms: 'Web forms',
    enterTheSlugOfTheWaitlist: 'Enter the slug of the waitlist page (/p/…).',
    aWaitlistNeedsAFieldMapped: 'A waitlist needs a field mapped to “Email”.',
    aWaitlistFormCannotBeA: 'A waitlist form cannot be a quiz.',
    formType: 'Form type',
    waitlistDoubleOptInByEmail: '“Waitlist”: double opt-in by email, position and referral.',
    classicFormCrmContact: 'Classic form (CRM contact)',
    waitlistBioPage: 'Waitlist (bio page)',
    waitlistPageSlug: 'Waitlist page slug',
    thePublishedBioPageThatHosts: 'The published bio page that hosts the list (/p/<slug>).',
    myLaunch: 'my-launch',
  },
};

export default en;
